import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useMusicPlayer } from '../MusicPlayerContext';
import { shareMusic } from '../utils/shareMusic';
import { downloadMusic } from '../utils/downloadMusic';
import { cacheMusicMetadata, cacheMusicFile, cacheLyrics, getCachedMusicMetadata, isFileCached } from '../utils/musicCache';
import { showUiHint } from '../utils/uiHint';
import { getMusicStreamUrl, getLyricsStreamUrl, getCoverStreamUrl, isSiteStreamEnabled } from '../utils/musicStreamUrls';
import { isInstrumentalMusic } from '../utils/musicMeta';
import { AdminContext } from '../AdminContext';
import { spaNavigate } from '../utils/spaNavigate';
import { getVideoFilePath, formatTime } from './musicPlayer/utils';
import { useAutoHideOnStop } from './musicPlayer/useAutoHideOnStop';
import PlayerPortals from './musicPlayer/PlayerPortals';
import { parseLRC, parseSRT } from './musicPlayer/lyricsParsers';
import { formatBitrate as formatBitrateRaw, formatSampleRate as formatSampleRateRaw } from './musicPlayer/mediaFormatters';
import { usePlayerHeightCssVar } from './musicPlayer/usePlayerHeightCssVar';
import { useVideoDocking } from './musicPlayer/useVideoDocking';

// MusicPlayer 调试日志：默认仅在开发环境输出，避免线上控制台刷屏
const MP_DEBUG = process.env.NODE_ENV === 'development';
const mpLog = (...args) => { if (MP_DEBUG) console.log(...args); };
const mpWarn = (...args) => { if (MP_DEBUG) console.warn(...args); };
const FADE_DURATION_MS = 3000;
const FADE_TICK_MS = 50;
const getFadeStep = (from, to) => Math.abs(to - from) * (FADE_TICK_MS / FADE_DURATION_MS);

// 避免在源码中直接出现旧地区码字面量（同时规避 eslint no-useless-concat）
const ZH_CN = 'zh\u002dCN';
const ZH_TW = 'zh\u002dTW';
const ZH_CN_LC = 'zh\u002dcn';
const ZH_TW_LC = 'zh\u002dtw';

const MusicPlayer = ({ 
  currentMusic = null, 
  playlist = [], 
  playing: externalPlaying = null,
  onNext = null, 
  onPrevious = null,
  onPlaylistChange = null,
  onRemoveFromPlaylist = null
}) => {
  const { i18n, t } = useTranslation();
  const { setPlaying: setContextPlaying, showPlaylist: contextShowPlaylist, setShowPlaylist: setContextShowPlaylist, setCurrentMusic: setContextCurrentMusic, setPlaylist: setContextPlaylist, removeFromPlaylist: contextRemoveFromPlaylist } = useMusicPlayer();
  
  // 使用 Context 中的 setPlaylist，如果没有则使用内部处理
  const handleSetPlaylist = setContextPlaylist || ((newPlaylist) => {
    // 如果没有 Context，尝试通过 onPlaylistChange 通知父组件
    // 这里只是兜底，实际应该通过 Context 管理
  });

  // 处理移除播放列表项：优先使用 Context 中的，否则使用 props 中的
  const handleRemoveFromPlaylist = useCallback((musicOrId) => {
    // 如果是 music 对象，提取 id
    const musicId = typeof musicOrId === 'object' && musicOrId?.id ? musicOrId.id : musicOrId;
    
    // 优先使用 Context 中的 removeFromPlaylist
    if (contextRemoveFromPlaylist) {
      contextRemoveFromPlaylist(musicId);
    } 
    // 否则使用 props 中的 onRemoveFromPlaylist
    else if (onRemoveFromPlaylist) {
      onRemoveFromPlaylist(musicOrId);
    }
  }, [contextRemoveFromPlaylist, onRemoveFromPlaylist]);
  const { isAdmin } = useContext(AdminContext);
  const playerRef = useRef(null);
  const playlistPanelRef = useRef(null);
  const settingsPanelRef = useRef(null);
  const settingsButtonRef = useRef(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const wakeLockRef = useRef(null); // Wake Lock API：防止屏幕锁定（仅在未手动锁屏时生效）
  const progressIntervalRef = useRef(null);
  const playHistoryIntervalRef = useRef(null);
  const progressRafRef = useRef(null); // 用 rAF 更新进度条 DOM，避免高频 setState 卡顿
  const fadeOutTimerRef = useRef(null);
  const userInitiatedChangeRef = useRef(false);
  const lastLoadedMusicIdRef = useRef(null);
  const pendingVideoLoadRef = useRef(null);
  const progressTrackRef = useRef(null);
  const progressFillRef = useRef(null);
  const progressKnobRef = useRef(null);
  const seekPreviewRef = useRef(null);
  const seekPreviewTimeRef = useRef(null);
  const seekPreviewTextRef = useRef(null);
  const seekPreviewTransRef = useRef(null);
  const seekPreviewTextAnimRef = useRef(null);
  const seekPreviewTransAnimRef = useRef(null);
  const seekPreviewRafRef = useRef(null);
  const seekPreviewXRef = useRef(0);
  const seekPreviewTargetXRef = useRef(0);
  const seekPreviewVisibleRef = useRef(false);
  const seekPreviewHideTimerRef = useRef(null);
  const seekPreviewLastTargetXRef = useRef(0);
  const seekPreviewLastTsRef = useRef(0);
  const seekPreviewVelRef = useRef(0);
  const currentSeekPreviewBoxRef = useRef(null); // 当前活动的气泡窗口引用
  const seekPreviewLyricIdxRef = useRef(-1); // 拖动进度条时：歌词 Hint 防抖（仅 idx 变化才更新）
  const isSeekingRef = useRef(false); // 拖动进度条时置 true，暂停 rAF 自动更新
  const progressTrackRectRef = useRef(null); // 缓存进度条 rect，避免频繁 getBoundingClientRect
  const progressTrackRectTimestampRef = useRef(0); // rect 缓存时间戳
  const seekPointerIdRef = useRef(null);
  const durationRef = useRef(0); // 避免 rAF 闭包读到旧的 duration=0
  const videoBaselineHeightRef = useRef(null); // PC：记录非全屏时视频可见高度，用于全屏字幕字号等比放大
  const videoSubtitleScaleRef = useRef(1); // PC：视频全屏时字幕字号倍率（缓存，避免每句字幕抖动）
  const videoFullscreenViewportKeyRef = useRef(''); // PC：字幕字号仅随窗口尺寸变化；窗口未变时锁死倍率
  const isLoadingRef = useRef(false); // 标记是否正在加载音频
  const lyricsContainerRef = useRef(null); // 歌词容器引用，用于滚动
  const lyricLineRefs = useRef({}); // 存储每行歌词的引用
  const lastUiTickRef = useRef(0); // 旧逻辑遗留：曾用于 UI 降频（现播放时不再 setCurrentTime）
  const lastTimeLabelSecRef = useRef(-1); // 当前时间文本（秒）缓存，避免每帧 formatTime
  const currentTimeTextRefDesktop = useRef(null);
  const currentTimeTextRefMobile = useRef(null);
  const currentLyricIndexRef = useRef(-1); // 歌词同步使用增量索引，避免每次全量扫描
  const isProgressTrackingRef = useRef(false);
  const lastLyricSyncMsRef = useRef(0); // 歌词同步节流（避免每帧处理）

  const [playing, setPlaying] = useState(false);
  const [lyricsViewportHeight, setLyricsViewportHeight] = useState(0); // 歌词容器可视高度，用于上下留白实现“始终居中”
  const [showFileInfo, setShowFileInfo] = useState(false);
  const [fileInfoPosition, setFileInfoPosition] = useState({ top: 0, left: 0 }); // 文件信息框位置
  const [currentPlayingFormat, setCurrentPlayingFormat] = useState(null); // 当前播放的文件格式信息
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 }); // 视频原始尺寸
  const [coverDimensions, setCoverDimensions] = useState({ width: 0, height: 0 }); // 封面原始尺寸
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false); // 视频窗口全屏（不改变底部播放器）
  const [isVideoWindowVisible, setIsVideoWindowVisible] = useState(false); // 仅在 video 实际播放时显示视频窗口
  const [showVideoWindow, setShowVideoWindow] = useState(true); // 手机：全屏按钮改为显示/隐藏视频窗口（播放不停止）
  const desktopControlsRowRef = useRef(null); // 桌面端“打开字幕区那一排”容器
  const volumeSliderWrapRef = useRef(null); // 音量滑条容器（用于测宽）
  const volumeSliderWidthRef = useRef(0); // 记录滑条宽度（隐藏后也能判断何时恢复）
  const [hideVolumeSlider, setHideVolumeSlider] = useState(false); // 挤占时隐藏音量滑条，保证按钮不换行
  // const [progressTrackWidth, setProgressTrackWidth] = useState(null); // 调试：进度条宽度(px)（已禁用）
  const [devViewport, setDevViewport] = useState({ w: 0, h: 0 }); // 开发：窗口尺寸
  const [isLgUp, setIsLgUp] = useState(() => {
    try { return typeof window !== 'undefined' ? window.innerWidth >= 1024 : true; } catch (_) { return true; }
  });
  const [isMdToLg, setIsMdToLg] = useState(() => {
    try {
      const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
      return w >= 768 && w < 1024;
    } catch (_) {
      return false;
    }
  });

  // 统一判断：是否为“手机模式”（< md）
  const isMobileViewport = !isLgUp && !isMdToLg;
  useEffect(() => {
    return () => {
      if (fadeOutTimerRef.current) {
        clearInterval(fadeOutTimerRef.current);
        fadeOutTimerRef.current = null;
      }
    };
  }, []);
  

  // 切歌时仅在“用户主动点播”时恢复默认显示
  useEffect(() => {
    if (!currentMusic?.id) return;
    if (userInitiatedChangeRef.current) {
      setShowVideoWindow(true);
      setManualHidden(false);
    }
    userInitiatedChangeRef.current = false;
  }, [currentMusic?.id]);

  // PC：在“非视频全屏”时记录一个基准视频高度，用于全屏时字幕字号等比放大
  useEffect(() => {
    try {
      if (isMobileViewport) {
        videoBaselineHeightRef.current = null;
        return;
      }
      const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
      const video = videoRef.current;
      if (!hasVideo || !video) return;
      if (!isVideoFullscreen && isVideoWindowVisible) {
        const h = video.clientHeight;
        if (h && Number.isFinite(h) && h > 0) {
          videoBaselineHeightRef.current = h;
        }
      }
    } catch (_) {}
  }, [currentMusic, isVideoFullscreen, isVideoWindowVisible, isMobileViewport]);

  // PC：进入视频全屏时计算一次字幕倍率并缓存，避免“当前一句大、下一句小”的抖动
  useEffect(() => {
    try {
      if (isMobileViewport) {
        videoSubtitleScaleRef.current = 1;
        videoFullscreenViewportKeyRef.current = '';
        return;
      }
      if (!isVideoFullscreen) {
        videoSubtitleScaleRef.current = 1;
        videoFullscreenViewportKeyRef.current = '';
        return;
      }
      const video = videoRef.current;
      if (!video) return;

      const refreshCurrentSubtitleFontSize = () => {
        try {
          // 只刷新字号：不触发整段淡入淡出动画，避免视觉闪烁
          if (!miniLyricTextRef.current) return;

          const baseFsRaw = Number(lyricSettingsRef.current?.fontSize ?? 16);
          const baseFs = Number.isFinite(baseFsRaw) ? baseFsRaw : 16;
          const scale = (!isMobileViewportRef.current && isVideoFullscreenRef.current) ? (videoSubtitleScaleRef.current || 1) : 1;

          miniLyricTextRef.current.style.fontSize = `${Math.round(baseFs * scale)}px`;

          if (miniLyricTransTextRef.current && miniLyricTransTextRef.current.style.display !== 'none') {
            const transFs = Math.max(10, Math.round(baseFs * 0.75 * scale));
            miniLyricTransTextRef.current.style.fontSize = `${transFs}px`;
          }
        } catch (_) {}
      };

      const compute = (force = false) => {
        try {
          // 你的要求：大屏大字、小屏小字；只要窗口尺寸不变，字体大小不能再变。
          const vw = window.innerWidth || 0;
          const vh = window.innerHeight || 0;
          const key = `${vw}x${vh}`;
          if (!force && key && key === videoFullscreenViewportKeyRef.current) {
            // 不重算倍率，但确保当前字幕字号刷新到“锁定倍率”
            refreshCurrentSubtitleFontSize();
            return;
          }
          videoFullscreenViewportKeyRef.current = key;

          // 阶梯式档位表：大屏大字、小屏小字；不再用比例计算，避免“看起来在算”且更可控
          // 只跟 viewport 宽度相关（布局无关），且只在 resize 时才会重新命中档位
          const next = (() => {
            const w = vw || 0;
            // 你可以按偏好继续微调这些档位
            if (w >= 2560) return 2.0;
            if (w >= 1920) return 1.75;
            if (w >= 1600) return 1.55;
            if (w >= 1440) return 1.45;
            if (w >= 1280) return 1.30;
            if (w >= 1024) return 1.15;
            return 1.0;
          })();
          videoSubtitleScaleRef.current = next;
          // 计算完成后立即刷新当前字幕字号（点击全屏按钮时也要立刻生效）
          refreshCurrentSubtitleFontSize();
        } catch (_) {}
      };

      // 进入全屏：立刻计算并锁定一次（只要 viewport 不变就不再变化）
      compute(true);
      // 下一帧再跑一次：如果 viewport 仍没变，会被 key 直接挡掉，不会回缩
      requestAnimationFrame(() => compute(false));

      // 仅当窗口尺寸变化时才允许更新（满足：大屏大字/小屏小字）
      const onResize = () => compute(false);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    } catch (_) {
      videoSubtitleScaleRef.current = 1;
      videoFullscreenViewportKeyRef.current = '';
    }
  }, [isVideoFullscreen, isMobileViewport, currentMusic?.id]);

  // 视频文件信息（codec/bitrate 等）懒加载：打开“信息”弹窗时触发
  const ensuredMediaInfoRef = useRef(new Set());
  const ensureVideoMediaInfo = useCallback(async () => {
    try {
      const m = currentMusic;
      const id = m?.id;
      if (!id) return;

      // 解析 metadata
      const meta = (() => {
        try {
          const raw = m?.metadata;
          return typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
        } catch (_) {
          return {};
        }
      })();

      if (!getVideoFilePath(m)) return;
      // 已有任意一个关键字段就不再请求
      if (meta.video_codec || meta.audio_codec || meta.video_bitrate || meta.audio_bitrate) return;

      if (ensuredMediaInfoRef.current.has(id)) return;
      ensuredMediaInfoRef.current.add(id);

      const resp = await fetch(`/api/music/${id}/media-info`, { cache: 'no-store' });
      const json = await resp.json().catch(() => null);
      if (!json?.success || !json?.data?.metadata) {
        // 失败允许下次重试
        ensuredMediaInfoRef.current.delete(id);
        return;
      }
      const newMeta = json.data.metadata;

      // 写回当前播放音乐（用于信息弹窗显示）
      setContextCurrentMusic((prev) => {
        if (!prev || prev.id !== id) return prev;
        return { ...prev, metadata: newMeta };
      });
    } catch (_) {
      // 失败允许下次重试
      try {
        const id = currentMusic?.id;
        if (id) ensuredMediaInfoRef.current.delete(id);
      } catch (_) {}
    }
  }, [currentMusic, setContextCurrentMusic]);
  
  // 同步内部状态到 Context
  const updatePlaying = (value) => {
    setPlaying(value);
    setContextPlaying(value);
  };
  
  // 如果外部传入 playing 状态，同步到内部状态
  useEffect(() => {
    if (externalPlaying !== null && externalPlaying !== playing) {
      if (externalPlaying && !playing) {
        // 外部要求播放
        // 如果正在加载音频，不执行播放（避免 AbortError）
        if (isLoadingRef.current) {
          return;
        }
        const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
        const mediaEl = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
        if (mediaEl && currentMusic) {
          // 检查媒体是否已加载
          if (mediaEl.readyState >= 2) {
            // 媒体已加载，可以播放
            const playPromise = mediaEl.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  updatePlaying(true);
                  if (!isProgressTrackingRef.current) {
                    startProgressTracking();
                  }
                  if (playHistoryIntervalRef.current === null) {
                    startPlayHistoryTracking();
                  }
                  recordPlay();
                  
                  // 播放时立即应用独占模式，尽量独占声卡
                  if ('mediaSession' in navigator) {
                    applyAudioFocusMode(true);
                  }
                })
                .catch(err => {
                  // 忽略 AbortError（当加载新音频时，之前的播放请求会被中断，这是正常行为）
                  if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
                    console.error('Auto play error:', err);
                  }
                });
            }
          } else {
            // 媒体未加载，等待加载完成后再播放
            const onLoadedMetadata = () => {
              try { mediaEl.removeEventListener('loadedmetadata', onLoadedMetadata); } catch (_) {}
              const playPromise = mediaEl.play();
              if (playPromise !== undefined) {
                playPromise
                  .then(() => {
                    updatePlaying(true);
                    if (!isProgressTrackingRef.current) {
                      startProgressTracking();
                    }
                    if (playHistoryIntervalRef.current === null) {
                      startPlayHistoryTracking();
                    }
                    recordPlay();
                    
                    // 播放时立即应用独占模式，尽量独占声卡
                    if ('mediaSession' in navigator) {
                      applyAudioFocusMode(true);
                    }
                  })
                  .catch(err => {
                    // 忽略 AbortError（当加载新音频时，之前的播放请求会被中断，这是正常行为）
                    if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
                      console.error('Auto play error:', err);
                    }
                  });
              }
            };
            mediaEl.addEventListener('loadedmetadata', onLoadedMetadata);
          }
        }
      } else if (!externalPlaying && playing) {
        // 外部要求暂停
        const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
        const mediaEl = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
        if (mediaEl) {
          mediaEl.pause();
          updatePlaying(false);
          stopProgressTracking();
          stopPlayHistoryTracking();
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPlaying, currentMusic, playing]);
  
  // 当 currentMusic 变化时，检查缓存状态
  useEffect(() => {
    if (currentMusic) {
      // 重置缓存状态跟踪
      wasCachedRef.current = false;
      checkCacheStatus(currentMusic);
    } else {
      setIsCached(false);
      wasCachedRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMusic?.id]);
  
  // 播放过程中定期检查缓存状态（因为缓存是异步的）
  useEffect(() => {
    if (!playing || !currentMusic) {
      return;
    }
    
    // 延迟一点再开始检查（给缓存一些时间）
    const initialTimeout = setTimeout(() => {
      checkCacheStatus(currentMusic);
    }, 1000);
    
    // 然后每隔2秒检查一次（最多检查10次，即20秒）
    let checkCount = 0;
    const maxChecks = 10;
    const interval = setInterval(() => {
      checkCount++;
      if (checkCount < maxChecks && currentMusic) {
        checkCacheStatus(currentMusic).then(() => {
          // 如果检查到已缓存，通知音乐列表
          // 注意：这里不能直接访问 isCached state，因为它是异步的
          // 所以我们在 checkCacheStatus 内部处理通知
        });
      } else {
        clearInterval(interval);
      }
    }, 2000);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, currentMusic?.id]);

  // 监听缓存清理事件，清空播放器中的缓存状态
  useEffect(() => {
    const handleCacheCleared = () => {
      setIsCached(false);
      // 如果当前正在播放音乐，重新检查缓存状态
      if (currentMusic) {
        checkCacheStatus(currentMusic);
      }
    };

    window.addEventListener('tenryu:cache-cleared', handleCacheCleared);
    return () => {
      window.removeEventListener('tenryu:cache-cleared', handleCacheCleared);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMusic]);
  
  const getCookieValue = (name) => {
    try {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [key, value] = cookie.trim().split('=');
        if (key === name) return value;
      }
    } catch (_) {}
    return null;
  };

  const setCookieValue = useCallback((name, value, maxAgeSeconds = 31536000) => {
    try {
      document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}`;
    } catch (_) {}
  }, []);

  const FADE_IN_OUT_COOKIE = 'music_player_fade_in_out';

  const getFadeInOutFromCookie = () => {
    const raw = getCookieValue(FADE_IN_OUT_COOKIE);
    if (raw === null || raw === undefined || raw === '') return undefined;
    if (raw === '1' || raw === 'true') return true;
    if (raw === '0' || raw === 'false') return false;
    return undefined;
  };

  // 从 localStorage 加载播放器设置
  const loadPlayerSettings = () => {
    try {
      const savedSettings = localStorage.getItem('musicPlayerSettings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        const fadeInOutCookie = getFadeInOutFromCookie();
        if (fadeInOutCookie !== undefined) {
          return { ...parsed, fadeInOut: fadeInOutCookie };
        }
        return parsed;
      }
    } catch (e) {
      console.warn('Failed to load player settings:', e);
    }
    return null;
  };

  // 保存播放器设置到 localStorage + cookie
  const savePlayerSettings = useCallback((settings) => {
    try {
      localStorage.setItem('musicPlayerSettings', JSON.stringify(settings));
      if (typeof settings?.fadeInOut === 'boolean') {
        setCookieValue(FADE_IN_OUT_COOKIE, settings.fadeInOut ? '1' : '0');
      }
    } catch (e) {
      console.warn('Failed to save player settings:', e);
    }
  }, [setCookieValue]);

  // 加载保存的设置
  const savedSettings = loadPlayerSettings();
  
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(savedSettings?.volume ?? 1);
  const [muted, setMuted] = useState(savedSettings?.muted ?? false);
  const [playMode, setPlayMode] = useState(savedSettings?.playMode ?? 'sequential'); // sequential, random, repeat
  // 使用 Context 中的 showPlaylist，如果没有则使用内部状态作为兜底
  const [internalShowPlaylist, setInternalShowPlaylist] = useState(false);
  const showPlaylist = contextShowPlaylist !== undefined ? contextShowPlaylist : internalShowPlaylist;
  const setShowPlaylist = contextShowPlaylist !== undefined ? setContextShowPlaylist : setInternalShowPlaylist;
  // max 字幕区（完整歌词列表）开关；mini 字幕区会在播放时自动显示
  const [showLyrics, setShowLyrics] = useState(false);
  // 说明：不再用 isMinimized 来做“停止时收缩”，避免停止/播放切换导致面板布局跳变。
  // 收缩/展开改为对整个面板做 transform（尺寸不变，仅可见区域变化）。
  const [isMinimized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lyrics, setLyrics] = useState([]);
  const lyricsRef = useRef([]); // 使用 ref 存储最新歌词，避免闭包问题
  // 播放期间不再用 state 驱动歌词行变化（会导致整组件频繁重渲染，按钮失灵）
  const currentLyricLineRef = useRef(-1);
  const appliedLyricHighlightRef = useRef({ index: -1, theme: null });
  const [loading, setLoading] = useState(false);
  const [isCached, setIsCached] = useState(false); // 当前歌曲是否已缓存
  const wasCachedRef = useRef(false); // 用于跟踪之前的缓存状态，避免重复发送事件
  // 播放速度设置已移除（固定 1.0）
  const [showSettings, setShowSettings] = useState(false); // 显示设置面板
  const [sleepTimer, setSleepTimer] = useState(null); // 睡眠定时器
  const [sleepTimerMinutes, setSleepTimerMinutes] = useState(savedSettings?.sleepTimerMinutes ?? 0); // 睡眠定时器分钟数
  const [fadeInOut, setFadeInOut] = useState(savedSettings?.fadeInOut ?? true); // 淡入淡出效果（默认开启）
  const [playlistLoop, setPlaylistLoop] = useState(savedSettings?.playlistLoop ?? true); // 播放列表循环播放（默认开启）
  const [visualFadeOutToken, setVisualFadeOutToken] = useState(0);
  const [visualFadeInToken, setVisualFadeInToken] = useState(0);
  const [isVisualSwitching, setIsVisualSwitching] = useState(false);
  const visualFadeMs = fadeInOut
    ? Math.round(Math.max(0, Math.min(1, muted ? 0 : volume)) * FADE_DURATION_MS)
    : 0;
  const getCoverUrlFromMusic = useCallback((music) => {
    if (!music) return null;
    return getCoverStreamUrl(music) || music.cover_image || music.cover_url || null;
  }, []);
  const getNextMusicForCover = useCallback(() => {
    if (!playlist || playlist.length === 0 || !currentMusic) return null;
    const currentIndex = playlist.findIndex((m) => m.id === currentMusic.id);
    if (currentIndex < 0) return null;
    if (playMode === 'repeat') return playlist[currentIndex] || null;
    if (playMode === 'random') return null;
    let nextIndex = currentIndex + 1;
    if (nextIndex >= playlist.length) {
      if (!playlistLoop) return null;
      nextIndex = 0;
    }
    return playlist[nextIndex] || null;
  }, [playlist, currentMusic, playMode, playlistLoop]);
  const hasVideoInCurrentMusic = !!getVideoFilePath(currentMusic);
  const coverUrl = getCoverUrlFromMusic(currentMusic);
  const nextCoverUrl = getCoverUrlFromMusic(getNextMusicForCover());
  const shouldShowCoverWindow = !hasVideoInCurrentMusic && !!coverUrl;
  const isSingleTrackMode = playMode === 'repeat' || (playlist && playlist.length === 1);
  const isSingleCoverMode = isSingleTrackMode && !hasVideoInCurrentMusic && !!coverUrl;
  const [coverLayerAUrl, setCoverLayerAUrl] = useState(null);
  const [coverLayerBUrl, setCoverLayerBUrl] = useState(null);
  const [coverLayerAType, setCoverLayerAType] = useState(null);
  const [coverLayerBType, setCoverLayerBType] = useState(null);
  const [coverLayerAOpacity, setCoverLayerAOpacity] = useState(1);
  const [coverLayerBOpacity, setCoverLayerBOpacity] = useState(0);
  const [coverLayerANoFadeIn, setCoverLayerANoFadeIn] = useState(false);
  const [coverLayerBNoFadeIn, setCoverLayerBNoFadeIn] = useState(false);
  const coverLayerInitRef = useRef(false);
  const coverActiveLayerRef = useRef('A');
  const coverFadeOutStartedRef = useRef(false);
  const coverFadeInRafRef = useRef(0);
  const lastCoverMusicIdRef = useRef(null);
  const lastVisualTypeRef = useRef(null);
  const noFadeResetRafRef = useRef(0);
  const scheduleCoverFadeIn = useCallback((layer) => {
    if (coverFadeInRafRef.current) cancelAnimationFrame(coverFadeInRafRef.current);
    coverFadeInRafRef.current = requestAnimationFrame(() => {
      if (layer === 'A') setCoverLayerAOpacity(1);
      else setCoverLayerBOpacity(1);
    });
  }, []);
  const scheduleCoverNoFadeIn = useCallback((layer) => {
    if (noFadeResetRafRef.current) cancelAnimationFrame(noFadeResetRafRef.current);
    if (layer === 'A') {
      setCoverLayerANoFadeIn(true);
      setCoverLayerAOpacity(1);
    } else {
      setCoverLayerBNoFadeIn(true);
      setCoverLayerBOpacity(1);
    }
    noFadeResetRafRef.current = requestAnimationFrame(() => {
      if (layer === 'A') setCoverLayerANoFadeIn(false);
      else setCoverLayerBNoFadeIn(false);
    });
  }, []);
  useEffect(() => {
    return () => {
      if (coverFadeInRafRef.current) cancelAnimationFrame(coverFadeInRafRef.current);
      coverFadeInRafRef.current = 0;
      if (noFadeResetRafRef.current) cancelAnimationFrame(noFadeResetRafRef.current);
      noFadeResetRafRef.current = 0;
    };
  }, []);
  useEffect(() => {
    const currentType = hasVideoInCurrentMusic ? 'video' : (coverUrl ? 'cover' : null);
    if (!currentType) {
      coverLayerInitRef.current = false;
      coverFadeOutStartedRef.current = false;
      lastCoverMusicIdRef.current = null;
      lastVisualTypeRef.current = null;
      setCoverLayerAUrl(null);
      setCoverLayerBUrl(null);
      setCoverLayerAType(null);
      setCoverLayerBType(null);
      setCoverLayerAOpacity(1);
      setCoverLayerBOpacity(0);
      return;
    }

    const musicId = currentMusic?.id ?? null;
    if (isSingleCoverMode) {
      coverLayerInitRef.current = true;
      coverFadeOutStartedRef.current = false;
      lastCoverMusicIdRef.current = musicId;
      lastVisualTypeRef.current = 'cover';
      coverActiveLayerRef.current = 'A';
      setCoverLayerAType('cover');
      setCoverLayerAUrl(coverUrl);
      setCoverLayerBType(null);
      setCoverLayerBUrl(null);
      setCoverLayerAOpacity(0);
      setCoverLayerBOpacity(0);
      return;
    }
    if (!fadeInOut) {
      coverLayerInitRef.current = true;
      coverFadeOutStartedRef.current = false;
      lastCoverMusicIdRef.current = musicId;
      lastVisualTypeRef.current = currentType;
      coverActiveLayerRef.current = 'A';
      setCoverLayerAType(currentType);
      setCoverLayerAUrl(currentType === 'cover' ? coverUrl : null);
      setCoverLayerBType(nextCoverUrl ? 'cover' : null);
      setCoverLayerBUrl(nextCoverUrl || null);
      setCoverLayerAOpacity(1);
      setCoverLayerBOpacity(0);
      return;
    }

    const activeLayer = coverActiveLayerRef.current;
    if (musicId && musicId === lastCoverMusicIdRef.current) {
      if (activeLayer === 'A') {
        setCoverLayerAType(currentType);
        setCoverLayerAUrl(currentType === 'cover' ? coverUrl : null);
        setCoverLayerBType(nextCoverUrl ? 'cover' : null);
        setCoverLayerBUrl(nextCoverUrl || null);
      } else {
        setCoverLayerBType(currentType);
        setCoverLayerBUrl(currentType === 'cover' ? coverUrl : null);
        setCoverLayerAType(nextCoverUrl ? 'cover' : null);
        setCoverLayerAUrl(nextCoverUrl || null);
      }
      return;
    }

    lastCoverMusicIdRef.current = musicId;
    const prevVisualType = lastVisualTypeRef.current;
    lastVisualTypeRef.current = currentType;
    coverFadeOutStartedRef.current = false;
    if (!coverLayerInitRef.current) {
      coverActiveLayerRef.current = 'A';
      setCoverLayerAType(currentType);
      setCoverLayerAUrl(currentType === 'cover' ? coverUrl : null);
      setCoverLayerBType(nextCoverUrl ? 'cover' : null);
      setCoverLayerBUrl(nextCoverUrl || null);
      setCoverLayerAOpacity(0);
      setCoverLayerBOpacity(0);
      coverLayerInitRef.current = true;
      scheduleCoverFadeIn('A');
      return;
    }

    const nextActive = activeLayer === 'A' ? 'B' : 'A';
    coverActiveLayerRef.current = nextActive;
    setCoverLayerAOpacity(0);
    setCoverLayerBOpacity(0);
    const noFadeInForCrossType = !!(prevVisualType && prevVisualType !== currentType);
    if (nextActive === 'A') {
      setCoverLayerAType(currentType);
      setCoverLayerAUrl(currentType === 'cover' ? coverUrl : null);
      setCoverLayerBType(nextCoverUrl ? 'cover' : null);
      setCoverLayerBUrl(nextCoverUrl || null);
      if (noFadeInForCrossType) scheduleCoverNoFadeIn('A');
      else scheduleCoverFadeIn('A');
    } else {
      setCoverLayerBType(currentType);
      setCoverLayerBUrl(currentType === 'cover' ? coverUrl : null);
      setCoverLayerAType(nextCoverUrl ? 'cover' : null);
      setCoverLayerAUrl(nextCoverUrl || null);
      if (noFadeInForCrossType) scheduleCoverNoFadeIn('B');
      else scheduleCoverFadeIn('B');
    }
  }, [currentMusic?.id, coverUrl, nextCoverUrl, fadeInOut, hasVideoInCurrentMusic, isSingleCoverMode, scheduleCoverFadeIn, scheduleCoverNoFadeIn]);
  useEffect(() => {
    if (!fadeInOut || !visualFadeOutToken) return;
    coverFadeOutStartedRef.current = true;
    if (coverActiveLayerRef.current === 'A') setCoverLayerAOpacity(0);
    else setCoverLayerBOpacity(0);
  }, [visualFadeOutToken, fadeInOut]);
  useEffect(() => {
    if (!fadeInOut || !visualFadeInToken) return;
    if (isSingleCoverMode) {
      coverActiveLayerRef.current = 'A';
      setCoverLayerANoFadeIn(false);
      setCoverLayerAOpacity(0);
      if (coverFadeInRafRef.current) cancelAnimationFrame(coverFadeInRafRef.current);
      coverFadeInRafRef.current = requestAnimationFrame(() => {
        setCoverLayerAOpacity(1);
      });
      return;
    }
    if (coverActiveLayerRef.current === 'A' && coverLayerANoFadeIn) {
      scheduleCoverNoFadeIn('A');
      return;
    }
    if (coverActiveLayerRef.current === 'B' && coverLayerBNoFadeIn) {
      scheduleCoverNoFadeIn('B');
      return;
    }
    scheduleCoverFadeIn(coverActiveLayerRef.current);
  }, [visualFadeInToken, fadeInOut, isSingleCoverMode, scheduleCoverFadeIn, scheduleCoverNoFadeIn, coverLayerANoFadeIn, coverLayerBNoFadeIn]);
  useEffect(() => {
    if (!currentMusic?.id) return;
    if (!fadeInOut) {
      setIsVisualSwitching(false);
      return;
    }
    if (!hasVideoInCurrentMusic && coverUrl) {
      setIsVisualSwitching(true);
      return;
    }
    setIsVisualSwitching(false);
  }, [currentMusic?.id, fadeInOut, hasVideoInCurrentMusic, coverUrl]);

  // 读取封面尺寸，用于封面窗口保持完整显示
  useEffect(() => {
    if (!coverUrl) {
      setCoverDimensions({ width: 0, height: 0 });
      return;
    }
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || img.width || 0;
      const h = img.naturalHeight || img.height || 0;
      setCoverDimensions({ width: w, height: h });
    };
    img.onerror = () => {
      setCoverDimensions({ width: 0, height: 0 });
    };
    img.src = coverUrl;
  }, [coverUrl]);
  
  // 获取当前界面语言，用于设置默认翻译目标
  const { i18n: i18nForInit } = useTranslation();
  const currentLangForInit = i18nForInit?.language || 'ja';
  
  // 根据当前界面语言生成默认翻译映射
  const getDefaultTranslationMap = () => {
    // 如果用户已经设置过，使用历史记录
    if (savedSettings?.lyricSettings?.translationMap) {
      return savedSettings.lyricSettings.translationMap;
    }
    
    // 否则根据当前界面语言设置默认值
    // 界面语言：ja/en/cn/tw -> 翻译目标：ja/en/zh
    // 缺省翻译的目标语言，设置成当前界面的语言
    let defaultTarget = 'zh'; // 中文界面默认翻译到中文
    if (currentLangForInit === 'ja') defaultTarget = 'ja';
    else if (currentLangForInit === 'en') defaultTarget = 'en';
    // cn/tw 使用 'zh'
    
    return {
      // 中文歌词：如果界面是中文，默认翻译到日语；否则翻译到界面语言
      zh: (currentLangForInit === 'cn' || currentLangForInit === 'tw') ? 'ja' : defaultTarget,
      // 日语歌词：如果界面是日语，默认翻译到中文；否则翻译到界面语言
      ja: currentLangForInit === 'ja' ? 'zh' : defaultTarget,
      // 英语歌词：如果界面是英语，默认翻译到中文；否则翻译到界面语言
      en: currentLangForInit === 'en' ? 'zh' : defaultTarget,
      // 其他歌词：默认翻译到界面语言
      other: defaultTarget
    };
  };
  
  const [lyricSettings, setLyricSettings] = useState({
    fontSize: savedSettings?.lyricSettings?.fontSize ?? 16,
    position: savedSettings?.lyricSettings?.position ?? 'center', // top, center, bottom
    showTranslation: savedSettings?.lyricSettings?.showTranslation ?? true, // 默认开启翻译字幕功能
    // 翻译字幕对应关系（按歌曲语言分类）
    // - zh: 中文歌词 -> 目标（ja/en）
    // - ja: 日语歌词 -> 目标（zh/en）
    // - en: 英语歌词 -> 目标（zh/ja）
    // - other: 其他歌词 -> 目标（zh/ja/en）
    // 其中 'zh' 会根据当前界面语言自动选择 cn 或 tw
    translationMap: getDefaultTranslationMap(),
    highlightTheme: savedSettings?.lyricSettings?.highlightTheme ?? 'purple', // amber | cyan | purple | green
    highlightGlow: savedSettings?.lyricSettings?.highlightGlow ?? 'medium' // low | medium | high
  });
  // eslint-disable-next-line no-unused-vars
  const [equalizerPreset, setEqualizerPreset] = useState(savedSettings?.equalizerPreset ?? 'normal'); // 均衡器预设
  const [audioContext, setAudioContext] = useState(null); // Web Audio API上下文
  // eslint-disable-next-line no-unused-vars
  const [audioSource, setAudioSource] = useState(null); // 音频源节点
  const [gainNodes, setGainNodes] = useState(null); // 增益节点数组
  // eslint-disable-next-line no-unused-vars
  const [lyricOffset, setLyricOffset] = useState(savedSettings?.lyricOffset ?? 0); // 歌词时间偏移（秒）
  
  // 音频输出设备相关状态
  const [audioOutputDevices, setAudioOutputDevices] = useState([]); // 可用的音频输出设备列表
  const [selectedAudioOutputId, setSelectedAudioOutputId] = useState(savedSettings?.audioOutputId ?? 'default'); // 当前选择的音频输出设备ID
  const [audioFocusMode, setAudioFocusMode] = useState(savedSettings?.audioFocusMode ?? 'exclusive'); // 音频焦点模式：'exclusive' 独占（默认） | 'shared' 共享

  // 停止播放时：关闭展开面板（避免残留占用空间/遮挡）
  useEffect(() => {
    if (playing) return;
    try { setShowPlaylist(false); } catch (_) {}
    try { setShowLyrics(false); } catch (_) {}
    try { setShowSettings(false); } catch (_) {}
  }, [playing, setShowPlaylist, setShowLyrics, setShowSettings]);

  // 自动隐藏逻辑：
  // - 手动暂停播放歌曲和歌曲播放完毕后，如果播放器不是Hover状态，启动自动隐藏倒计时（10秒）
  // - 开始播放歌曲，停止自动隐藏倒计时
  // - 播放器区域内交互会重置倒计时；鼠标在播放器内部时暂停倒计时
  const [isPlayerHovering, setIsPlayerHovering] = useState(false);
  const { autoHidden, bump: bumpAutoHideCountdown, setAutoHidden } = useAutoHideOnStop({
    playing,
    isFullscreen,
    // 鼠标在播放器内部：暂停倒计时；鼠标离开：开始计时
    suspend: isPlayerHovering,
    delayMs: 10_000
  });
  const [manualHidden, setManualHidden] = useState(false);

  useEffect(() => {
    // 进入全屏时，恢复显示播放器面板
    if (isFullscreen) setManualHidden(false);
  }, [isFullscreen]);

  const handlePlaylistChange = useCallback((music, index) => {
    userInitiatedChangeRef.current = true;
    if (onPlaylistChange) onPlaylistChange(music, index);
  }, [onPlaylistChange]);

  // 视频窗口是否应该显示：视频在播 or 无视频时播放中 + 有封面
  const activeMediaElement = hasVideoInCurrentMusic ? videoRef.current : audioRef.current;
  const isStopped = !!currentMusic && !playing && !!activeMediaElement?.ended;
  const shouldShowVideoWindow = !autoHidden && !manualHidden && showVideoWindow && !isStopped && (
    hasVideoInCurrentMusic
    || isVideoWindowVisible
    || shouldShowCoverWindow
    || isVisualSwitching
  );

  // 将播放器实际高度写入 CSS 变量，供视频浮窗对齐/页面留白使用
  usePlayerHeightCssVar({ playerRef, isFullscreen });

  const { videoWindowStyle, videoDockStyle, dockingReady } = useVideoDocking({
    currentMusic,
    isFullscreen,
    shouldShowVideoWindow,
    isVideoFullscreen,
    playerRef,
    getVideoFilePath,
    videoDimensions,
    coverDimensions,
    showCover: shouldShowCoverWindow
  });

  // 以 video 的真实播放状态控制“视频窗口是否可见”
  useEffect(() => {
    const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
    const video = videoRef.current;
    if (!hasVideo || !video) {
      setIsVideoWindowVisible(false);
      return;
    }

    const onPlay = () => setIsVideoWindowVisible(true);
    const onPause = () => setIsVideoWindowVisible(false);
    const onEnded = () => setIsVideoWindowVisible(false);
    const onEmptied = () => setIsVideoWindowVisible(false);

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('emptied', onEmptied);

    // 初始同步一次（避免状态滞后）
    setIsVideoWindowVisible(!video.paused && !video.ended);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('emptied', onEmptied);
    };
  }, [currentMusic]);
  
  // 当设置改变时保存到 localStorage
  useEffect(() => {
    savePlayerSettings({
      volume,
      muted,
      playMode,
      sleepTimerMinutes,
      fadeInOut,
      lyricSettings,
      equalizerPreset,
      lyricOffset,
      playlistLoop,
      audioOutputId: selectedAudioOutputId,
      audioFocusMode: audioFocusMode
    });
  }, [volume, muted, playMode, sleepTimerMinutes, fadeInOut, lyricSettings, equalizerPreset, lyricOffset, playlistLoop, selectedAudioOutputId, audioFocusMode, savePlayerSettings]);
  
  // 应用保存的音量设置（muted/volume 变化时同步到媒体元素）
  useEffect(() => {
    const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
    const mediaEl = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
    if (!mediaEl) return;
    mediaEl.volume = muted ? 0 : volume;
    // 兜底：避免切换 hasVideo 时另一个元素残留音量
    if (audioRef.current && audioRef.current !== mediaEl) audioRef.current.volume = muted ? 0 : volume;
    if (videoRef.current && videoRef.current !== mediaEl) videoRef.current.volume = muted ? 0 : volume;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muted, volume, currentMusic?.id]);

  // 站内 UI 语言码：ja/en/cn/tw（注意：歌词翻译接口仍使用外部中文地区码）
  const currentLang = i18n.language || 'cn';

  const resolveTargetTranslationLang = React.useCallback((music) => {
    try {
      const src = String(music?.language_category || '').trim();
      const map = lyricSettingsRef.current?.translationMap || {};
      let group = 'other';
      if (src === 'cn' || src === 'tw' || src === ZH_CN || src === ZH_TW) group = 'zh';
      else if (src === 'ja') group = 'ja';
      else if (src === 'en') group = 'en';

      const chosen = map[group] || (group === 'zh' ? 'ja' : 'zh');
      if (chosen === 'ja') return 'ja';
      if (chosen === 'en') return 'en';
      // chosen === 'zh'
      return String(currentLang || '') === 'tw' ? ZH_TW : ZH_CN;
    } catch (_) {
      return String(currentLang || '') === 'tw' ? ZH_TW : ZH_CN;
    }
  }, [currentLang]);

  // mini 字幕：播放时展示（只显示当前高亮一句）
  const hasLyrics = !!currentMusic?.lyrics_type && lyrics.length > 0;

  // 取消面板歌词：不显示 mini；字幕统一叠加在视频/封面上
  const shouldShowMiniLyrics = false;
  const shouldShowVideoSubtitles = !isMinimized && playing && hasLyrics && (hasVideoInCurrentMusic || shouldShowCoverWindow);

  // 高亮样式（固定枚举，避免 Tailwind 动态类被 purge）
  const getHighlightThemeClasses = () => {
    switch (lyricSettings.highlightTheme) {
      case 'cyan':
        return {
          text: 'text-cyan-200',
          bg: 'bg-gradient-to-r from-cyan-500/20 via-cyan-400/10 to-transparent',
          ring: 'ring-1 ring-cyan-400/25',
          miniBorder: 'border-cyan-400/20',
          miniText: 'text-cyan-100',
          miniTrans: 'text-cyan-100/70',
          glowRGB: '34,211,238' // cyan-400
        };
      case 'green':
        return {
          text: 'text-emerald-200',
          bg: 'bg-gradient-to-r from-emerald-500/20 via-emerald-400/10 to-transparent',
          ring: 'ring-1 ring-emerald-400/25',
          miniBorder: 'border-emerald-400/20',
          miniText: 'text-emerald-100',
          miniTrans: 'text-emerald-100/70',
          glowRGB: '52,211,153' // emerald-400
        };
      case 'purple':
        return {
          text: 'text-fuchsia-200',
          bg: 'bg-gradient-to-r from-fuchsia-500/18 via-purple-500/10 to-transparent',
          ring: 'ring-1 ring-fuchsia-400/25',
          miniBorder: 'border-fuchsia-400/20',
          miniText: 'text-fuchsia-100',
          miniTrans: 'text-fuchsia-100/70',
          glowRGB: '232,121,249' // fuchsia-400
        };
      case 'amber':
      default:
        return {
          text: 'text-amber-200',
          bg: 'bg-gradient-to-r from-amber-500/20 via-amber-400/10 to-transparent',
          ring: 'ring-1 ring-amber-400/25',
          miniBorder: 'border-amber-400/20',
          miniText: 'text-amber-200',
          miniTrans: 'text-amber-100/70',
          glowRGB: '251,191,36' // amber-400
        };
    }
  };

  const getGlowAlpha = () => {
    switch (lyricSettings.highlightGlow) {
      case 'low':
        return 0.18;
      case 'high':
        return 0.42;
      case 'medium':
      default:
        return 0.28;
    }
  };

  const highlightTheme = getHighlightThemeClasses();
  const highlightGlowAlpha = getGlowAlpha();
  const highlightThemeRef = useRef(highlightTheme);
  const highlightGlowAlphaRef = useRef(highlightGlowAlpha);
  const lyricSettingsRef = useRef(lyricSettings);
  const showLyricsRef = useRef(showLyrics);
  const lyricOffsetRef = useRef(lyricOffset);
  // 关键：歌词更新链路大量使用 ref/rAF，避免闭包读到旧的全屏/视口状态导致“下一句字幕变小”
  const isVideoFullscreenRef = useRef(isVideoFullscreen);
  const isMobileViewportRef = useRef(isMobileViewport);
  const miniLyricTextRef = useRef(null);
  const miniLyricTransTextRef = useRef(null);
  const lyricFadeOutTimerRef = useRef(null);
  const lyricScrollTimerRef = useRef(null); // 高亮动画结束后再滚动，避免“边滚边淡”

  useEffect(() => {
    highlightThemeRef.current = highlightTheme;
    highlightGlowAlphaRef.current = highlightGlowAlpha;
  }, [highlightTheme, highlightGlowAlpha]);
  useEffect(() => {
    lyricSettingsRef.current = lyricSettings;
  }, [lyricSettings]);
  useEffect(() => {
    showLyricsRef.current = showLyrics;
  }, [showLyrics]);
  useEffect(() => {
    lyricOffsetRef.current = lyricOffset;
  }, [lyricOffset]);
  useEffect(() => {
    isVideoFullscreenRef.current = isVideoFullscreen;
  }, [isVideoFullscreen]);
  useEffect(() => {
    isMobileViewportRef.current = isMobileViewport;
  }, [isMobileViewport]);

  const addClasses = (el, cls) => {
    if (!el || !cls) return;
    String(cls).split(/\s+/).filter(Boolean).forEach(c => el.classList.add(c));
  };
  const removeClasses = (el, cls) => {
    if (!el || !cls) return;
    String(cls).split(/\s+/).filter(Boolean).forEach(c => el.classList.remove(c));
  };

  // 让高亮歌词的过渡更明显：opacity + transform + boxShadow（不依赖 Tailwind scale 类，避免 transform 冲突）
  const applyLyricAnimBase = (el) => {
    if (!el) return;
    el.style.willChange = 'opacity, transform, box-shadow, filter';
    el.style.transition = 'opacity 260ms cubic-bezier(0.22, 1, 0.36, 1), transform 260ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 260ms ease, filter 260ms ease';
  };

  const scrollLyricToCenter = (lineEl) => {
    const container = lyricsContainerRef.current;
    if (!container || !lineEl) return;
    const containerRect = container.getBoundingClientRect();
    const lineRect = lineEl.getBoundingClientRect();
    const lineTopInContainer = (lineRect.top - containerRect.top) + container.scrollTop;
    const containerHeight = container.clientHeight;
    const lineHeight = lineEl.clientHeight;
    const targetScrollTop = lineTopInContainer - (containerHeight / 2) + (lineHeight / 2);
    const maxScrollTop = container.scrollHeight - containerHeight;
    const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));
    container.scrollTo({ top: finalScrollTop, behavior: 'smooth' });
  };

  const clearLyricHighlight = () => {
    if (lyricScrollTimerRef.current) {
      clearTimeout(lyricScrollTimerRef.current);
      lyricScrollTimerRef.current = null;
    }
    const prevIdx = appliedLyricHighlightRef.current?.index ?? -1;
    if (prevIdx >= 0) {
      const prevEl = lyricLineRefs.current[prevIdx];
      const prevTheme = appliedLyricHighlightRef.current?.theme;
      if (prevEl) {
        if (prevTheme) {
          removeClasses(prevEl, prevTheme.text);
          removeClasses(prevEl, prevTheme.bg);
          removeClasses(prevEl, prevTheme.ring);
        }
        prevEl.classList.remove('font-semibold');
        prevEl.style.boxShadow = 'none';
        prevEl.style.opacity = '1';
        prevEl.style.transform = '';
        prevEl.style.filter = '';
        prevEl.style.fontSize = `${lyricSettingsRef.current?.fontSize ?? 16}px`;
        const t = prevEl.querySelector('[data-lyric-trans]');
        if (t) {
          // 复位为灰（移除之前高亮翻译色）
          if (prevTheme) removeClasses(t, prevTheme.miniTrans);
          addClasses(t, 'text-gray-500');
        }
      }
    }
    appliedLyricHighlightRef.current = { index: -1, theme: null };
    currentLyricLineRef.current = -1;
  };

  const updateMiniLyric = (idx) => {
    if (!miniLyricTextRef.current) return;
    const lines = lyricsRef.current || [];
    const has = lines.length > 0;
    const safeIdx = has ? (idx >= 0 ? idx : 0) : -1;
    const line = safeIdx >= 0 ? lines[safeIdx] : null;
    const theme = highlightThemeRef.current;
    const inVideoSubtitleMode = !!shouldShowVideoSubtitles;

    const isCjkLike = (s) => {
      try {
        const text = String(s || '');
        // CJK Unified Ideographs + Hiragana/Katakana + Hangul
        return /[\u3040-\u30ff\u3400-\u9fff\uac00-\ud7af]/.test(text);
      } catch (_) {
        return false;
      }
    };

    const buildOuterStrokeShadow = (strokePx = 2) => {
      const c = 'rgba(0,0,0,0.95)';
      const p = strokePx;
      // 8-direction hard outline + a soft drop shadow
      const outline = [
        `${-p}px ${-p}px 0 ${c}`,
        `0 ${-p}px 0 ${c}`,
        `${p}px ${-p}px 0 ${c}`,
        `${-p}px 0 0 ${c}`,
        `${p}px 0 0 ${c}`,
        `${-p}px ${p}px 0 ${c}`,
        `0 ${p}px 0 ${c}`,
        `${p}px ${p}px 0 ${c}`
      ].join(', ');
      const drop = `0 3px 10px rgba(0,0,0,0.85)`;
      return `${outline}, ${drop}`;
    };

    // 迷你字幕：切换时做更明显的淡入淡出（opacity + 轻微上浮/缩放）
    miniLyricTextRef.current.style.willChange = 'opacity, transform, filter, text-shadow';
    miniLyricTextRef.current.style.transition = 'opacity 240ms cubic-bezier(0.22, 1, 0.36, 1), transform 240ms cubic-bezier(0.22, 1, 0.36, 1), filter 240ms ease';
    miniLyricTextRef.current.style.opacity = '0';
    miniLyricTextRef.current.style.transform = 'translateY(6px) scale(0.985)';
    miniLyricTextRef.current.style.filter = 'blur(0.6px)';
    if (miniLyricTransTextRef.current) {
      miniLyricTransTextRef.current.style.willChange = 'opacity, transform, filter';
      miniLyricTransTextRef.current.style.transition = 'opacity 240ms cubic-bezier(0.22, 1, 0.36, 1), transform 240ms cubic-bezier(0.22, 1, 0.36, 1), filter 240ms ease';
      miniLyricTransTextRef.current.style.opacity = '0';
      miniLyricTransTextRef.current.style.transform = 'translateY(6px) scale(0.985)';
      miniLyricTransTextRef.current.style.filter = 'blur(0.6px)';
    }

    // 文本
    miniLyricTextRef.current.textContent = line?.text || '';
    miniLyricTextRef.current.classList.remove('text-gray-300/70');
    removeClasses(miniLyricTextRef.current, theme?.miniText);
    if (inVideoSubtitleMode) {
      // 视频字幕：固定白色（不要主题色），并根据字符类型调整字间距避免描边互相影响
      addClasses(miniLyricTextRef.current, 'text-white');
      miniLyricTextRef.current.style.letterSpacing = isCjkLike(line?.text) ? '0.12em' : '0.02em';
      // 字号也由播放器设置决定（与歌词字号设置联动）
      const fs = Number(lyricSettingsRef.current?.fontSize ?? 16);
      const safeFs = Number.isFinite(fs) ? fs : 16;
      // PC 视频全屏：使用缓存倍率，避免每句字幕因布局瞬变出现"忽大忽小"
      const scale = (!isMobileViewportRef.current && isVideoFullscreenRef.current) ? (videoSubtitleScaleRef.current || 1) : 1;
      miniLyricTextRef.current.style.fontSize = `${Math.round(safeFs * scale)}px`;
    } else {
      if (idx >= 0) {
        addClasses(miniLyricTextRef.current, theme?.miniText);
      } else {
        addClasses(miniLyricTextRef.current, 'text-gray-300/70');
      }
      miniLyricTextRef.current.style.letterSpacing = '';
      miniLyricTextRef.current.style.fontSize = '';
    }

    // 外描边（用 text-shadow 叠加模拟，轮廓更“在字外面”） + 阴影 +（可选）光晕
    const glow = highlightGlowAlphaRef.current ?? 0.28;
    const baseStroke = buildOuterStrokeShadow(2);
    const glowPart = (idx >= 0 && theme?.glowRGB)
      ? `, 0 0 14px rgba(${theme.glowRGB}, ${Math.max(0.12, glow - 0.1)})`
      : '';
    miniLyricTextRef.current.style.textShadow = `${baseStroke}${glowPart}`;
    // 禁用 WebkitTextStroke（它会“吃进字内”，不符合外描边预期）
    try { miniLyricTextRef.current.style.webkitTextStroke = '0px transparent'; } catch (_) {}
    try { miniLyricTextRef.current.style.WebkitTextStroke = '0px transparent'; } catch (_) {}

    // 翻译
    if (miniLyricTransTextRef.current) {
      const showTrans = !!lyricSettingsRef.current?.showTranslation;
      if (showTrans && line?.translation) {
        miniLyricTransTextRef.current.style.display = '';
        miniLyricTransTextRef.current.textContent = line.translation;
        removeClasses(miniLyricTransTextRef.current, theme?.miniTrans);
        if (inVideoSubtitleMode) {
          addClasses(miniLyricTransTextRef.current, 'text-white');
          miniLyricTransTextRef.current.style.letterSpacing = isCjkLike(line?.translation) ? '0.10em' : '0.02em';
          // 翻译行字号按主字幕做比例缩放
          const fs = Number(lyricSettingsRef.current?.fontSize ?? 16);
          const safeFs = Number.isFinite(fs) ? fs : 16;
          const scale = (!isMobileViewportRef.current && isVideoFullscreenRef.current) ? (videoSubtitleScaleRef.current || 1) : 1;
          const transFs = Math.max(10, Math.round(safeFs * 0.75 * scale));
          miniLyricTransTextRef.current.style.fontSize = `${transFs}px`;
        } else {
          addClasses(miniLyricTransTextRef.current, theme?.miniTrans);
          miniLyricTransTextRef.current.style.letterSpacing = '';
          miniLyricTransTextRef.current.style.fontSize = '';
        }
        // 译文也加同样的外描边+阴影（稍弱）
        const transStroke = buildOuterStrokeShadow(2);
        const transGlowPart = (idx >= 0 && theme?.glowRGB)
          ? `, 0 0 10px rgba(${theme.glowRGB}, ${Math.max(0.10, glow - 0.14)})`
          : '';
        miniLyricTransTextRef.current.style.textShadow = `${transStroke}${transGlowPart}`;
        try { miniLyricTransTextRef.current.style.webkitTextStroke = '0px transparent'; } catch (_) {}
        try { miniLyricTransTextRef.current.style.WebkitTextStroke = '0px transparent'; } catch (_) {}
      } else {
        miniLyricTransTextRef.current.style.display = 'none';
        miniLyricTransTextRef.current.textContent = '';
      }
    }

    requestAnimationFrame(() => {
      if (miniLyricTextRef.current) {
        miniLyricTextRef.current.style.opacity = '1';
        miniLyricTextRef.current.style.transform = 'translateY(0px) scale(1)';
        miniLyricTextRef.current.style.filter = 'blur(0px)';
      }
      if (miniLyricTransTextRef.current && miniLyricTransTextRef.current.style.display !== 'none') {
        miniLyricTransTextRef.current.style.opacity = '1';
        miniLyricTransTextRef.current.style.transform = 'translateY(0px) scale(1)';
        miniLyricTransTextRef.current.style.filter = 'blur(0px)';
      }
    });
  };

  const applyLyricHighlight = (nextIdx, options = {}) => {
    const { force = false, ensureCenter = false } = options || {};
    const prevIdx = appliedLyricHighlightRef.current?.index ?? -1;
    if (!force && nextIdx === prevIdx) {
      if (ensureCenter && showLyricsRef.current && nextIdx >= 0) {
        const el = lyricLineRefs.current[nextIdx];
        if (el) requestAnimationFrame(() => scrollLyricToCenter(el));
      }
      return;
    }

    // 复位旧行（先淡出再复位，做渐隐渐显）
    if (prevIdx >= 0) {
      const prevEl = lyricLineRefs.current[prevIdx];
      const prevTheme = appliedLyricHighlightRef.current?.theme;
      if (prevEl) {
        applyLyricAnimBase(prevEl);
        prevEl.style.opacity = '0';
        prevEl.style.transform = 'translateY(-6px) scale(0.985)';
        prevEl.style.filter = 'blur(0.6px)';
        if (lyricFadeOutTimerRef.current) {
          clearTimeout(lyricFadeOutTimerRef.current);
          lyricFadeOutTimerRef.current = null;
        }
        lyricFadeOutTimerRef.current = setTimeout(() => {
          // 如果这一行已经不再是当前高亮，则复位为普通行
          if (currentLyricLineRef.current !== prevIdx && prevEl) {
            if (prevTheme) {
              removeClasses(prevEl, prevTheme.text);
              removeClasses(prevEl, prevTheme.bg);
              removeClasses(prevEl, prevTheme.ring);
            }
            prevEl.classList.remove('font-semibold');
            prevEl.style.boxShadow = 'none';
            prevEl.style.fontSize = `${lyricSettingsRef.current?.fontSize ?? 16}px`;
            const t = prevEl.querySelector('[data-lyric-trans]');
            if (t) {
              if (prevTheme) removeClasses(t, prevTheme.miniTrans);
              addClasses(t, 'text-gray-500');
            }
            prevEl.style.opacity = '1';
            prevEl.style.transform = '';
            prevEl.style.filter = '';
          }
        }, 220);
      }
    }

    // 应用新行
    const nextEl = nextIdx >= 0 ? lyricLineRefs.current[nextIdx] : null;
    const theme = highlightThemeRef.current;
    if (nextEl && theme) {
      applyLyricAnimBase(nextEl);
      // 先设为透明，再在下一帧淡入
      nextEl.style.opacity = '0';
      nextEl.style.transform = 'translateY(8px) scale(0.98)';
      nextEl.style.filter = 'blur(0.6px)';
      addClasses(nextEl, theme.text);
      addClasses(nextEl, theme.bg);
      addClasses(nextEl, theme.ring);
      nextEl.classList.add('font-semibold');
      // 先弱光晕，再在下一帧增强（更明显）
      const glow = highlightGlowAlphaRef.current ?? 0.28;
      nextEl.style.boxShadow = `0 0 0 rgba(${theme.glowRGB}, ${Math.max(0.12, glow - 0.16)})`;
      nextEl.style.fontSize = `${(lyricSettingsRef.current?.fontSize ?? 16) + 2}px`;
      const t = nextEl.querySelector('[data-lyric-trans]');
      if (t) {
        t.classList.remove('text-gray-500');
        addClasses(t, theme.miniTrans);
      }
      requestAnimationFrame(() => {
        if (nextEl) {
          nextEl.style.opacity = '1';
          nextEl.style.transform = 'translateY(0px) scale(1)';
          nextEl.style.filter = 'blur(0px)';
          nextEl.style.boxShadow = `0 0 26px rgba(${theme.glowRGB}, ${glow})`;
        }
      });
    }

    appliedLyricHighlightRef.current = { index: nextIdx, theme };
    currentLyricLineRef.current = nextIdx;

    // mini 文本
    updateMiniLyric(nextIdx);

    // 居中滚动：等淡入完成后再滚动，确保淡入淡出在“原地”发生（滚动前）
    if ((ensureCenter || showLyricsRef.current) && showLyricsRef.current) {
      if (lyricScrollTimerRef.current) {
        clearTimeout(lyricScrollTimerRef.current);
        lyricScrollTimerRef.current = null;
      }
      const targetIdx = nextIdx;
      lyricScrollTimerRef.current = setTimeout(() => {
        lyricScrollTimerRef.current = null;
        if (!showLyricsRef.current) return;
        if (currentLyricLineRef.current !== targetIdx) return;
        const el = lyricLineRefs.current[targetIdx];
        if (el) scrollLyricToCenter(el);
      }, 260);
    }
  };

  // mini 字幕显示/主题/翻译设置变化时，刷新一次内容与样式（不靠 state 触发逐帧渲染）
  useEffect(() => {
    // 视频模式字幕叠加在视频上，也需要刷新
    if (!(shouldShowMiniLyrics || shouldShowVideoSubtitles)) return;
    updateMiniLyric(currentLyricLineRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowMiniLyrics, shouldShowVideoSubtitles, lyrics.length, lyricSettings.showTranslation, lyricSettings.highlightTheme, lyricSettings.highlightGlow]);

  // 统一控件样式：让按钮/图标/滑块更精致一致
  const cx = (...classes) => classes.filter(Boolean).join(' ');

  // 按钮点击时显示浮动 hint 的辅助函数
  const handleButtonClickWithHint = (e, originalHandler, hintText) => {
    if (originalHandler) {
      originalHandler(e);
    }
    if (hintText && e.currentTarget) {
      showUiHint(hintText, { anchorEl: e.currentTarget, duration: 1200 });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const smallIconButtonBase = 'h-8 w-8 inline-flex items-center justify-center rounded-lg transition-all duration-150 select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900 active:scale-[0.98]';
  const goldRGB = '251,191,36'; // 琥珀金（用于拖动预览浮窗的统一金色风格）
  const goldRGB2 = '245,158,11'; // amber-500，用于更明显的金色层次

  // 播放列表展开时：点击播放器区域外收起（避免页面切换/误触影响）
  useEffect(() => {
    if (!showPlaylist) return;
    const onDocMouseDown = (e) => {
      const playerEl = playerRef.current;
      if (!playerEl) return;
      // 如果点击的是【+】按钮（添加到播放列表按钮），不关闭播放列表
      if (e.target.closest('[data-add-to-playlist-btn="true"]')) {
        return;
      }
      if (!playerEl.contains(e.target)) {
        setShowPlaylist(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [showPlaylist, setShowPlaylist]);

  // 点击播放器空白区域：展开/收起播放列表
  const handlePlayerBlankClick = (e) => {
    const target = e.target;

    // 如果文件信息框显示，点击播放器任何地方都关闭它
    if (showFileInfo) {
      const button = target.closest('button');
      // 如果点击的不是信息按钮，关闭信息框
      if (!button || (!button.textContent?.includes(t('player.unknownFormat')) && !button.textContent?.includes('MB') && !button.textContent?.includes('KB') && !button.textContent?.includes('GB'))) {
        setShowFileInfo(false);
      }
    }
    if (target && typeof target.closest === 'function') {
      // 点击控件不触发展开/收起（避免与按钮/滑块等冲突）
      if (target.closest('button, a, input, select, textarea, [data-no-toggle]')) return;
      // 点击播放列表区域本身不触发展开/收起
      if (playlistPanelRef.current && playlistPanelRef.current.contains(target)) return;
      // 点击设置面板区域本身不触发展开/收起
      if (settingsPanelRef.current && settingsPanelRef.current.contains(target)) return;
    }

    // 只有点到真正空白处才关闭设置面板（避免“按钮要点多次”）
    if (showSettings) {
      setShowSettings(false);
      return;
    }
    setShowPlaylist(prev => !prev);
  };

  // ===== 防止“滚动穿透到主页”统一处理 =====
  const getScrollableAncestorWithinPlayer = (startEl) => {
    try {
      const root = playerRef.current;
      let el = startEl;
      while (el && el !== root) {
        if (el instanceof HTMLElement) {
          const style = window.getComputedStyle(el);
          const oy = (style.overflowY || '').toLowerCase();
          const canScrollY = (oy === 'auto' || oy === 'scroll') && (el.scrollHeight > el.clientHeight + 1);
          if (canScrollY) return el;
        }
        el = el && el.parentElement ? el.parentElement : null;
      }
      // 兜底：root 自身若可滚动也算
      if (root && root instanceof HTMLElement) {
        const style = window.getComputedStyle(root);
        const oy = (style.overflowY || '').toLowerCase();
        const canScrollY = (oy === 'auto' || oy === 'scroll') && (root.scrollHeight > root.clientHeight + 1);
        if (canScrollY) return root;
      }
    } catch (_) {}
    return null;
  };

  // 用原生 wheel 事件（passive:false）拦截“滚动穿透到主页”
  useEffect(() => {
    const root = playerRef.current;
    if (!root) return;

    const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

    const onWheel = (ev) => {
      try {
        if (!root.contains(ev.target)) return;

        // 找到播放器内部最近的可滚动容器
        const scrollEl = getScrollableAncestorWithinPlayer(ev.target);

        // 统一阻止默认行为，避免页面滚动；然后手动滚动 scrollEl（若存在）
        // 注意：这里必须依赖 passive:false，否则 preventDefault 无效
        ev.preventDefault();
        ev.stopPropagation();
        if (typeof ev.stopImmediatePropagation === 'function') ev.stopImmediatePropagation();

        if (!scrollEl) return;

        let dy = Number(ev.deltaY || 0);
        if (!dy) return;

        // deltaMode: 0=像素, 1=行, 2=页
        if (ev.deltaMode === 1) dy *= 16;
        else if (ev.deltaMode === 2) dy *= (scrollEl.clientHeight || 300);

        const maxTop = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
        const nextTop = clamp(scrollEl.scrollTop + dy, 0, maxTop);
        scrollEl.scrollTop = nextTop;
      } catch (_) {}
    };

    // capture + passive:false：确保能拦截并 preventDefault
    root.addEventListener('wheel', onWheel, { capture: true, passive: false });
    return () => {
      try { root.removeEventListener('wheel', onWheel, { capture: true }); } catch (_) {
        // 旧浏览器不支持 options 形式 removeEventListener 的情况下兜底
        try { root.removeEventListener('wheel', onWheel, true); } catch (_) {}
      }
    };
  // 依赖：当 DOM 结构（可滚动区域）变化时重新挂载，避免 root 变化导致失效
  }, [currentMusic?.id, showLyrics, showPlaylist, showSettings, isFullscreen]);

  // 设置面板：点击外部/按 ESC 关闭
  useEffect(() => {
    if (!showSettings) return;

    const onDocMouseDown = (e) => {
      const panel = settingsPanelRef.current;
      const btn = settingsButtonRef.current;
      if (!panel) return;

      // 点击设置按钮本身不关闭（由按钮逻辑处理）
      if (btn && btn.contains(e.target)) return;
      // 点击面板内部不关闭
      if (panel.contains(e.target)) return;

      setShowSettings(false);
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowSettings(false);
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showSettings]);

  // 获取当前语言的标题
  const getCurrentLanguageTitle = (music) => {
    if (!music) return '';
    switch (currentLang) {
      case 'cn':
        return music.title_zh_cn || music.title;
      case 'tw':
        return music.title_zh_tw || music.title;
      case 'ja':
        return music.title_ja || music.title;
      case 'en':
        return music.title_en || music.title;
      default:
        return music.title;
    }
  };

  // 初始化Web Audio API
  // eslint-disable-next-line no-unused-vars
  const initAudioContext = () => {
    if (!audioContext) {
      try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioContextClass();
        setAudioContext(ctx);
        return ctx;
      } catch (error) {
        console.error('AudioContext not supported:', error);
        return null;
      }
    }
    return audioContext;
  };

  // 枚举可用的音频输出设备
  const enumerateAudioOutputDevices = async () => {
    try {
      const isMobile = isMobileDevice();
      
      // 检查是否支持 setSinkId API
      if (!audioRef.current || typeof audioRef.current.setSinkId !== 'function') {
        mpLog('[音频输出] 浏览器不支持 setSinkId API', isMobile ? '[移动端可能不支持]' : '');
        // 在移动端，大多数情况下不支持，但仍返回默认设备选项
        const defaultList = [{ deviceId: 'default', label: t('player.defaultDevice') }];
        if (isMobile) {
          // 移动端添加说明
          defaultList[0].label = t('player.defaultDeviceMobileNote');
        }
        setAudioOutputDevices(defaultList);
        return defaultList;
      }

      // 请求媒体设备权限（某些浏览器需要）
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        // 权限被拒绝或不需要权限，继续尝试枚举设备
        mpLog('[音频输出] 媒体设备权限:', e.message);
      }

      // 枚举所有音频设备
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      // 过滤出音频输出设备（kind === 'audiooutput'）
      const outputDevices = devices
        .filter(device => device.kind === 'audiooutput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `${t('player.audioOutputDeviceLabel')} ${device.deviceId.substring(0, 8)}...`,
          groupId: device.groupId
        }));

      // 添加默认设备选项
      const deviceList = [
        { deviceId: 'default', label: isMobile ? t('player.defaultDeviceMobile') : t('player.defaultDevice') },
        ...outputDevices
      ];

      setAudioOutputDevices(deviceList);
      return deviceList;
    } catch (error) {
      console.error('[音频输出] 枚举设备失败:', error);
      // 返回至少包含默认设备的列表
      const isMobile = isMobileDevice();
      const defaultList = [{ 
        deviceId: 'default', 
        label: isMobile ? t('player.defaultDeviceMobileNote') : t('player.defaultDevice')
      }];
      setAudioOutputDevices(defaultList);
      return defaultList;
    }
  };

  // 设置音频输出设备
  const setAudioOutputDevice = async (deviceId) => {
    const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
    const mediaEl = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
    if (!mediaEl) {
      mpWarn('[音频输出] 媒体元素未初始化');
      return false;
    }

    // 检查是否支持 setSinkId API
    if (typeof mediaEl.setSinkId !== 'function') {
      mpWarn('[音频输出] 浏览器不支持 setSinkId API（Chrome/Edge 需要）');
      showUiHint(t('hints.browserNotSupportAudioOutput'), { duration: 2000 });
      return false;
    }

    try {
      // 如果是默认设备，使用空字符串
      const sinkId = deviceId === 'default' ? '' : deviceId;
      await mediaEl.setSinkId(sinkId);
      setSelectedAudioOutputId(deviceId);
      mpLog('[音频输出] 已切换到设备:', deviceId === 'default' ? t('player.defaultDevice') : deviceId);
      return true;
    } catch (error) {
      console.error('[音频输出] 设置设备失败:', error);
      showUiHint(t('player.audioOutputDeviceFailed', { error: error.message || t('player.unknownError') }), { duration: 2000 });
      return false;
    }
  };

  // 检测是否为移动设备
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
  };

  // 应用音频焦点模式（通过 MediaSession API）
  const applyAudioFocusMode = (forceExclusive = false) => {
    if (!('mediaSession' in navigator)) {
      return;
    }

    try {
      const isMobile = isMobileDevice();
      const shouldExclusive = forceExclusive || audioFocusMode === 'exclusive';
      
      // 设置音频焦点策略
      // 注意：Web API 中无法真正实现独占模式，但可以通过设置优先级来影响行为
      if (shouldExclusive) {
        // 独占模式：尝试设置更高的优先级，尽量独占声卡
        // 在移动端，可以通过 MediaSession 的播放状态来影响音频焦点
        
        // 设置播放动作处理器（确保播放时获得焦点）
        navigator.mediaSession.setActionHandler('play', () => {
          const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
          const mediaEl = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
          if (mediaEl) mediaEl.play().catch(console.error);
        });
        
        // 设置播放状态为 playing，尝试获得音频焦点
        if (playing) {
          navigator.mediaSession.playbackState = 'playing';
        }
        
        // 在移动端，尝试通过播放状态来影响音频焦点
        if (isMobile && playing) {
          // 设置播放状态为 playing，某些移动浏览器可能会据此调整音频焦点优先级
          navigator.mediaSession.playbackState = 'playing';
        }
        
        // 可以添加更多独占模式的设置（不输出控制台日志）
      } else {
        // 共享模式：正常行为
        // 在移动端，允许其他应用同时播放音频
        // 不输出控制台日志
      }
    } catch (error) {
      // 静默：不刷控制台
    }
  };

  // 应用均衡器预设
  const applyEqualizer = (preset) => {
    if (!audioContext || !audioSource) return;

    // 均衡器预设（10段均衡器，频率：31, 62, 125, 250, 500, 1k, 2k, 4k, 8k, 16k Hz）
    const presets = {
      normal: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      pop: [0, 0, 2, 4, 3, 0, -1, -1, 0, 1],
      rock: [4, 3, -3, -4, -2, 2, 4, 4, 3, 3],
      classical: [3, 2, 0, -2, -3, -3, -2, 0, 2, 3],
      jazz: [2, 1, 0, 1, 2, 1, 0, 1, 2, 2],
      bass: [6, 5, 4, 2, 0, -2, -3, -4, -4, -4],
      treble: [-4, -4, -3, -2, 0, 2, 4, 5, 6, 6],
      vocal: [-2, -1, 0, 1, 2, 3, 2, 1, 0, -1]
    };

    const gains = presets[preset] || presets.normal;
    
    // 创建或更新增益节点
    if (!gainNodes) {
      const nodes = gains.map(gain => {
        const node = audioContext.createGain();
        node.gain.value = 1 + (gain / 10); // 转换为增益值
        return node;
      });
      setGainNodes(nodes);
      
      // 连接音频源到增益节点链，再到目标
      let currentSource = audioSource;
      nodes.forEach(node => {
        currentSource.connect(node);
        currentSource = node;
      });
      nodes[nodes.length - 1].connect(audioContext.destination);
    } else {
      gainNodes.forEach((node, index) => {
        node.gain.value = 1 + (gains[index] / 10);
      });
    }
  };

  // 加载音乐
  useEffect(() => {
    if (!currentMusic || !audioRef.current) return;
    const musicId = currentMusic.id ?? null;
    const videoLayerReady = coverLayerAType === 'video' || coverLayerBType === 'video';
    if (hasVideoInCurrentMusic && !videoLayerReady) {
      pendingVideoLoadRef.current = musicId;
      return;
    }
    if (lastLoadedMusicIdRef.current === musicId && pendingVideoLoadRef.current !== musicId) {
      return;
    }
    pendingVideoLoadRef.current = null;
    lastLoadedMusicIdRef.current = musicId;
    // 只在“切歌/首次载入”时调用 loadMusic
    // 注意：不要把 externalPlaying 放进依赖里，否则“暂停→播放”会触发 loadMusic 重载音频并重置到 0
    loadMusic(currentMusic, externalPlaying === true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMusic, hasVideoInCurrentMusic, coverLayerAType, coverLayerBType]);
  
  // 当 duration 确定后，更新码流（从实际文件计算）
  useEffect(() => {
    if (duration > 0 && currentPlayingFormat && currentPlayingFormat.file_size) {
      const calculatedBitrate = Math.round((currentPlayingFormat.file_size * 8) / duration);
      // 只有当计算出的码流与当前不同时才更新（避免无限循环）
      if (!currentPlayingFormat.bitrate || Math.abs(currentPlayingFormat.bitrate - calculatedBitrate) > 1000) {
        setCurrentPlayingFormat(prev => ({
          ...prev,
          bitrate: calculatedBitrate
        }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, currentPlayingFormat?.file_size]);

  // 当均衡器预设改变时，重新应用
  useEffect(() => {
    if (audioContext && audioSource && equalizerPreset && equalizerPreset !== 'normal') {
      applyEqualizer(equalizerPreset);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equalizerPreset, audioContext, audioSource]);

  // 当翻译设置改变时，重新加载歌词
  useEffect(() => {
    if (currentMusic && currentMusic.lyrics_type) {
      loadLyrics(null, currentMusic.lyrics_type, currentMusic);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lyricSettings.showTranslation, lyricSettings.translationMap, currentMusic, currentLang]);

  // Media Session API 支持 (车载蓝牙/锁屏控制)
  useEffect(() => {
    if ('mediaSession' in navigator) {
      if (currentMusic) {
        const title = getCurrentLanguageTitle(currentMusic);
        const artist = currentMusic.artist || 'Unknown Artist';
        const album = currentMusic.album || '';
        
        // 构建封面图片数组
        const artwork = [];
        if (currentMusic.cover_url) {
          // 添加不同尺寸的封面图，虽然我们只有原图，但可以声明不同尺寸让系统适配
          const sizes = ['96x96', '128x128', '192x192', '256x256', '384x384', '512x512'];
          sizes.forEach(size => {
            artwork.push({
              src: currentMusic.cover_url,
              sizes: size,
              type: 'image/jpeg'
            });
          });
        }

        navigator.mediaSession.metadata = new MediaMetadata({
          title: title,
          artist: artist,
          album: album,
          artwork: artwork
        });

        // 设置动作处理器
        try {
          navigator.mediaSession.setActionHandler('play', () => {
            const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
            const mediaEl = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
            if (mediaEl) {
              mediaEl.play().catch(console.error);
              // 播放时应用独占模式
              if (audioFocusMode === 'exclusive') {
                applyAudioFocusMode(true);
              }
            }
          });
          navigator.mediaSession.setActionHandler('pause', () => {
             const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
             const mediaEl = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
             if (mediaEl) {
              mediaEl.pause();
            }
          });
          navigator.mediaSession.setActionHandler('previoustrack', () => {
            if (onPrevious) onPrevious();
          });
          navigator.mediaSession.setActionHandler('nexttrack', () => {
            if (onNext) onNext();
          });
          navigator.mediaSession.setActionHandler('seekto', (details) => {
            const mediaElement = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
            if (mediaElement && details.seekTime !== undefined) {
              mediaElement.currentTime = details.seekTime;
            }
          });
          
          // 如果当前正在播放且是独占模式，立即应用独占模式
          if (playing && audioFocusMode === 'exclusive') {
            applyAudioFocusMode(true);
          }
        } catch (error) {
          console.warn('Media Session actions warning:', error);
        }
      } else {
        // 没有音乐时清空 metadata
        navigator.mediaSession.metadata = null;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMusic, onNext, onPrevious, currentLang]);

  // 更新播放状态到 Media Session，并在播放时应用独占模式
  useEffect(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = playing ? "playing" : "paused";
      
      // 播放时自动应用独占模式，尽量独占声卡（音乐播放程序应该独占）
      if (playing) {
        // 强制应用独占模式，确保播放时获得音频焦点
        // 延迟一点应用，确保音频已经开始播放
        setTimeout(() => {
          applyAudioFocusMode(true);
        }, 100);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  // 初始化时枚举音频输出设备
  useEffect(() => {
    enumerateAudioOutputDevices();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 当选择的音频输出设备改变时，应用设置
  useEffect(() => {
    const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
    const mediaEl = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
    if (mediaEl && selectedAudioOutputId && mediaEl.src) {
      // 只有在媒体已加载时才切换设备
      setAudioOutputDevice(selectedAudioOutputId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAudioOutputId]);

  // 当音频焦点模式改变时，应用设置
  useEffect(() => {
    applyAudioFocusMode();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioFocusMode]);

  // 播放时自动应用独占模式（音乐播放程序应该尽量独占声卡）
  useEffect(() => {
    if (playing && audioFocusMode === 'exclusive') {
      // 播放时强制应用独占模式，确保获得音频焦点
      applyAudioFocusMode(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  // Wake Lock API：防止屏幕自动锁定（仅在播放时且页面可见时）
  useEffect(() => {
    // 请求唤醒锁（防止自动锁屏）
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator && !wakeLockRef.current && !document.hidden) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          // 不输出控制台日志
          
          // 监听唤醒锁释放事件（用户手动锁屏或系统释放）
          wakeLockRef.current.addEventListener('release', () => {
            // 不输出控制台日志
            wakeLockRef.current = null;
          });
        }
      } catch (err) {
        // 用户拒绝或其他错误（静默处理，不影响播放）
        // 不输出控制台日志
        wakeLockRef.current = null;
      }
    };

    // 释放唤醒锁
    const releaseWakeLock = async () => {
      try {
        if (wakeLockRef.current) {
          await wakeLockRef.current.release();
          wakeLockRef.current = null;
          // 不输出控制台日志
        }
      } catch (err) {
        // 不输出控制台日志
        wakeLockRef.current = null;
      }
    };

    // 根据播放状态管理唤醒锁
    if (playing && !document.hidden) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    // 清理函数：组件卸载时释放
    return () => {
      releaseWakeLock();
    };
  }, [playing]);

  // 监听页面可见性变化：页面隐藏时释放唤醒锁，页面显示且正在播放时重新请求
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // 页面隐藏时释放唤醒锁（节省电量，但音频仍会继续播放）
        if (wakeLockRef.current) {
          try {
            await wakeLockRef.current.release();
            wakeLockRef.current = null;
            // 不输出控制台日志
          } catch (err) {
            // 不输出控制台日志
            wakeLockRef.current = null;
          }
        }
      } else {
        // 页面显示且正在播放时，重新请求唤醒锁
        if (playing && 'wakeLock' in navigator && !wakeLockRef.current) {
          try {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
            // 不输出控制台日志
            
            wakeLockRef.current.addEventListener('release', () => {
              // 不输出控制台日志
              wakeLockRef.current = null;
            });
          } catch (err) {
            // 不输出控制台日志
            wakeLockRef.current = null;
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [playing]);

  // max 字幕区打开/样式变化时：重应用当前高亮，并确保居中（不依赖 state 驱动）
  useEffect(() => {
    if (!showLyrics) return;
    requestAnimationFrame(() => {
      const idx = currentLyricLineRef.current;
      if (idx >= 0) {
        applyLyricHighlight(idx, { force: true, ensureCenter: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLyrics, lyricsViewportHeight, lyricSettings.showTranslation, lyricSettings.fontSize, lyricSettings.highlightTheme, lyricSettings.highlightGlow, lyrics.length]);

  // 监听歌词容器高度变化，用于计算上下留白（让高亮行尽量固定在中间）
  useEffect(() => {
    if (!showLyrics) return;

    const container = lyricsContainerRef.current;
    if (!container) return;

    let rafId = 0;
    let lastH = -1;

    const updateHeightNow = () => {
      const h = container.clientHeight || 0;
      if (h === lastH) return;
      lastH = h;
      setLyricsViewportHeight(h);
    };

    const scheduleUpdateHeight = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        updateHeightNow();
      });
    };

    scheduleUpdateHeight();

    // ResizeObserver 更精准；无则退化到 window resize
    if (typeof ResizeObserver !== 'undefined') {
      // 避免 ResizeObserver loop：回调里只做 rAF 调度
      const ro = new ResizeObserver(() => scheduleUpdateHeight());
      ro.observe(container);
      return () => {
        if (rafId) cancelAnimationFrame(rafId);
        ro.disconnect();
      };
    }

    window.addEventListener('resize', scheduleUpdateHeight);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', scheduleUpdateHeight);
    };
  }, [showLyrics]);

  // 桌面端按钮行：永远不换行；若挤占则隐藏音量滑条
  useEffect(() => {
    const row = desktopControlsRowRef.current;
    if (!row) return;

    // 非 lg+：允许换行两排；此时不做“挤占隐藏滑条保单行”测量
    if (!isLgUp) {
      return;
    }

    let rafId = 0;

    const measure = () => {
      // 更新滑条宽度缓存（可见时测一次）
      const sliderWrap = volumeSliderWrapRef.current;
      if (sliderWrap && sliderWrap.offsetParent !== null) {
        const w = sliderWrap.getBoundingClientRect().width || 0;
        if (w > 0) volumeSliderWidthRef.current = w;
      }

      const overflow = row.scrollWidth > (row.clientWidth + 1);

      // 正在显示滑条但溢出了：隐藏
      if (!hideVolumeSlider && overflow) {
        setHideVolumeSlider(true);
        return;
      }

      // 已隐藏滑条：有足够空间则恢复
      if (hideVolumeSlider) {
        const free = row.clientWidth - row.scrollWidth; // 不含滑条时的剩余空间
        const need = (volumeSliderWidthRef.current || 140) + 12; // 预留一点间距
        if (free > need) {
          setHideVolumeSlider(false);
        }
      }
    };

    const schedule = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        measure();
      });
    };

    schedule();

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => schedule());
      try { ro.observe(row); } catch (_) {}
    }
    window.addEventListener('resize', schedule);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', schedule);
      if (ro) {
        try { ro.disconnect(); } catch (_) {}
      }
    };
  }, [hideVolumeSlider, currentMusic?.id, showLyrics, showPlaylist, playMode, playlist?.length, isLgUp]);

  // 监听断点：lg(>=1024) 才启用“单行+隐藏滑条”策略
  useEffect(() => {
    let rafId = 0;
    const update = () => {
      const w = window.innerWidth || 0;
      const nextLg = w >= 1024;
      const nextMdToLg = w >= 768 && w < 1024;
      setIsLgUp(prev => (prev === nextLg ? prev : nextLg));
      setIsMdToLg(prev => (prev === nextMdToLg ? prev : nextMdToLg));
      // 进入 lg+ 时，重置一次“挤占隐藏”状态，让测量逻辑重新接管
      if (nextLg) setHideVolumeSlider(false);
    };
    const schedule = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        update();
      });
    };
    schedule();
    window.addEventListener('resize', schedule);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', schedule);
    };
  }, []);

  // 调试 Hint：显示当前进度条宽度（已备注/禁用）

  // 开发模式：右下角显示窗口尺寸（避免 ResizeObserver loop，用 rAF 节流）
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    let rafId = 0;
    let lastW = -1;
    let lastH = -1;

    const updateNow = () => {
      const w = window.innerWidth || 0;
      const h = window.innerHeight || 0;
      if (w === lastW && h === lastH) return;
      lastW = w;
      lastH = h;
      setDevViewport({ w, h });
    };

    const schedule = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        updateNow();
      });
    };

    schedule();
    window.addEventListener('resize', schedule);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', schedule);
    };
  }, []);

  // 尝试加载音频文件的辅助函数
  const tryLoadAudioFile = async (audio, filePath, timeout = 10000) => {
    return new Promise((resolve, reject) => {
      const onLoadedMetadata = () => {
        audio.removeEventListener('loadedmetadata', onLoadedMetadata);
        audio.removeEventListener('error', onError);
        resolve();
      };
      const onError = (e) => {
        console.error('Audio load error:', e, 'File path:', filePath);
        audio.removeEventListener('loadedmetadata', onLoadedMetadata);
        audio.removeEventListener('error', onError);
        reject(new Error(t('player.audioLoadFailed', { file: filePath })));
      };
      audio.addEventListener('loadedmetadata', onLoadedMetadata);
      audio.addEventListener('error', onError);
      
      // 设置超时
      setTimeout(() => {
        if (audio.readyState === 0) {
          audio.removeEventListener('loadedmetadata', onLoadedMetadata);
          audio.removeEventListener('error', onError);
          reject(new Error(t('player.audioLoadTimeout', { file: filePath })));
        }
      }, timeout);
    });
  };

  // 检查当前歌曲的缓存状态
  const checkCacheStatus = async (music) => {
    if (!music || !music.id) {
      setIsCached(false);
      wasCachedRef.current = false;
      return false;
    }
    
    try {
      // 检查 IndexedDB 中是否有元数据
      const cachedMetadata = await getCachedMusicMetadata(music.id);
      if (!cachedMetadata) {
        setIsCached(false);
        return false;
      }
      
      // 检查媒体文件是否已缓存（有视频则以视频为准）
      const videoPath = getVideoFilePath(music);
      // 使用流式URL检查缓存
      const streamUrlToCheck = getStreamUrlForCache(music, currentPlayingFormat, videoPath);
      if (streamUrlToCheck) {
        const isAudioCached = await isFileCached(streamUrlToCheck);
        const wasCached = wasCachedRef.current;
        setIsCached(isAudioCached);
        wasCachedRef.current = isAudioCached;
        
        // 如果从未缓存变为已缓存，通知音乐列表
        if (isAudioCached && !wasCached) {
          try {
            window.dispatchEvent(new CustomEvent('tenryu:music-cached', { 
              detail: { musicId: music.id } 
            }));
          } catch (_) {}
        }
        
        return isAudioCached;
      } else {
        setIsCached(false);
        wasCachedRef.current = false;
        return false;
      }
    } catch (error) {
      console.warn('Failed to check cache status:', error);
      setIsCached(false);
      wasCachedRef.current = false;
      return false;
    }
  };

  // 使用工具函数生成流式传输URL（隐藏实际文件路径）
  const getStreamUrl = useCallback((music, format = 'auto') => {
    return getMusicStreamUrl(music, format);
  }, []);

  // 使用工具函数生成歌词文件的流式传输URL
  const getLyricsStreamUrlLocal = useCallback((music) => {
    return getLyricsStreamUrl(music);
  }, []);

  // 使用工具函数生成封面图片的流式传输URL
  const getCoverStreamUrlLocal = useCallback((music) => {
    return getCoverStreamUrl(music);
  }, []);

  // 根据音乐和当前播放格式生成流式URL（用于缓存）
  // 注意：视频不做离线缓存，播放视频时缓存对应的音频文件
  const getStreamUrlForCache = useCallback((music, currentFormat, videoFilePath) => {
    if (!music || !music.id) return null;
    // 如果有视频，不缓存视频，而是缓存对应的音频文件（确保离线时有歌可以听）
    if (videoFilePath) {
      // 优先使用 Opus 格式，如果没有则使用 MP3，最后使用自动选择
      const metadata = music.metadata ? (typeof music.metadata === 'string' ? JSON.parse(music.metadata) : music.metadata) : null;
      const convertedFormats = metadata?.convertedFormats || [];
      const opusFormat = convertedFormats.find(f => f.format === 'opus');
      if (opusFormat && opusFormat.path) {
        return getMusicStreamUrl(music, 'opus');
      }
      const mp3Format = convertedFormats.find(f => f.format === 'mp3');
      if (mp3Format && mp3Format.path) {
        return getMusicStreamUrl(music, 'mp3');
      }
      // 如果没有转码格式，使用自动选择（会回退到主文件）
      return getMusicStreamUrl(music, 'auto');
    }
    // 如果有当前播放格式信息，使用对应格式
    if (currentFormat?.format) {
      return getMusicStreamUrl(music, currentFormat.format);
    }
    // 否则使用自动选择
    return getMusicStreamUrl(music, 'auto');
  }, []);

  // 加载音乐文件
  const loadMusic = async (music, shouldAutoPlay = false) => {
    if (!music || !music.file_path) return;

    isLoadingRef.current = true; // 标记开始加载
    setLoading(true);
    const audio = audioRef.current;
    const video = videoRef.current;
    const videoFilePath = getVideoFilePath(music);

    // 切歌时重置 UI/歌词同步状态，避免残留导致卡顿或跳行
    lastUiTickRef.current = 0;
    currentLyricIndexRef.current = -1;
    
    // 在加载新音频之前，先暂停并重置当前音频，避免 AbortError
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
        // 重置音频元素，清除之前的加载状态
        audio.load();
      } catch (e) {
        // 忽略重置时的错误
      }
    }
    
    // 如果有视频，也重置视频元素
    if (video && videoFilePath) {
      try {
        video.pause();
        video.currentTime = 0;
        video.load();
      } catch (e) {
        // 忽略重置时的错误
      }
    }
    
    // 只有在不是自动播放时才设置为 false
    if (!shouldAutoPlay) {
      updatePlaying(false);
    }
    setDuration(0);
    clearLyricHighlight();
    updateMiniLyric(-1);
    setCurrentPlayingFormat(null); // 重置当前播放格式信息
    setVideoDimensions({ width: 0, height: 0 }); // 重置视频尺寸
    setIsVideoFullscreen(false); // 切歌时退出视频全屏

    try {
      // ===== 播放权限预检查（给免费会员新歌提示）=====
      // 说明：媒体元素（<audio>/<video>）在 403 时只触发 error 事件，无法读取到后端返回的 JSON message。
      // 所以在设置 src 之前先探测一次，若被会员规则拦截则直接提示并停止加载。
      // 注意：管理员应该跳过此检查，因为管理员可以播放所有音乐。
      if (!isAdmin) {
        try {
          const resp = await fetch(`/api/music/${music.id}/playback/check`, {
            method: 'GET',
            cache: 'no-store',
            credentials: 'include'
          });
          const data = await resp.json().catch(() => ({}));

          // 兼容两种后端返回：
          // - 旧版：用 403 表示禁止播放
          // - 新版：返回 200，但 data.allowed=false，避免浏览器控制台出现 403 噪音
          const allowed = !!(resp.ok && data?.data?.allowed !== false);
          if (!allowed) {
            const code = String(data?.code || data?.data?.code || '');
            if (code === 'PLAYBACK_REQUIRES_MEMBERSHIP') {
              // 使用 i18n 文案（后端 message 作为兜底）
              showUiHint(t('player.playbackRequiresMembershipHint') || data?.message || t('auth.membershipFreeDesc'), { duration: 2600 });
            } else if (code === 'ACCESS_DENIED') {
              showUiHint(t('player.playbackAccessDeniedHint') || data?.message || t('player.unknownError'), { duration: 2200 });
            } else {
              const msg = data?.message || data?.error || `HTTP ${resp.status}`;
              showUiHint(msg, { duration: 2000 });
            }
            setLoading(false);
            isLoadingRef.current = false;
            updatePlaying(false);
            return;
          }
        } catch (e) {
          // 网络异常时不阻塞播放（交给后续媒体加载错误处理）
        }
      }

      // ===== 若存在视频文件：优先使用视频作为媒体源（视频里已包含音乐）=====
      // 但：生产环境经常出现视频文件缺失/改名的情况。
      // 这里必须“失败回退到音频”，否则会像你日志里那样：mp4 404 -> 整首歌无法播放。
      if (video && videoFilePath) {
        // 规则：只要 mp4 真实存在，就优先用视频；只有在确实不可用时才回退到音频。
        // 注意：当全站 stream 开关关闭时，getStreamUrl(music,'video') 可能等同于 videoFilePath（直链）。
        // 这里额外提供一个“强制 API stream”的兜底 URL，避免直链因为静态路由/权限策略导致 404 时误判为“文件不存在”。
        const directVideoUrl = videoFilePath;
        // 重要：当全站 stream 开关关闭时，不允许再偷偷走强制 API（否则无法暴露/解决真实的直链问题）
        const apiVideoUrl = (isSiteStreamEnabled() && music && music.id) ? `/api/music/${music.id}/stream?format=video` : null;
        // 确保音频不播放、不占用状态
        if (audio) {
          try {
            audio.pause();
            audio.currentTime = 0;
            audio.removeAttribute('src');
            audio.load();
          } catch (_) {}
        }

        const tryLoadVideo = async (url) => {
          if (!url) return false;
          try {
            video.src = url;
            video.volume = muted ? 0 : volume;
            if (fadeInOut) {
              video.volume = 0;
            }
            video.playbackRate = 1.0;
            await new Promise((resolve, reject) => {
              const onLoaded = () => {
                video.removeEventListener('loadedmetadata', onLoaded);
                video.removeEventListener('error', onError);
                if (video.videoWidth && video.videoHeight) {
                  setVideoDimensions({ width: video.videoWidth, height: video.videoHeight });
                }
                resolve();
              };
              const onError = () => {
                video.removeEventListener('loadedmetadata', onLoaded);
                video.removeEventListener('error', onError);
                reject(new Error('video_load_failed'));
              };
              video.addEventListener('loadedmetadata', onLoaded);
              video.addEventListener('error', onError);
            });
            return true;
          } catch (_) {
            return false;
          }
        };

        // 优先尝试直链视频；直链失败再尝试 API stream；两者都失败才回退到音频
        let loadedVideo = false;
        loadedVideo = await tryLoadVideo(directVideoUrl);
        if (!loadedVideo && apiVideoUrl && apiVideoUrl !== directVideoUrl) {
          console.warn('[Video] Direct URL failed, trying API stream...', { directVideoUrl, apiVideoUrl, musicId: music?.id });
          loadedVideo = await tryLoadVideo(apiVideoUrl);
        }

        if (!loadedVideo) {
          console.warn('[Video] Video not available, fallback to audio:', {
            musicId: music?.id,
            videoFilePath,
            directVideoUrl,
            apiVideoUrl
          });
          try {
            video.pause();
            video.currentTime = 0;
            video.removeAttribute('src');
            video.load();
          } catch (_) {}
          // 回退：继续走后续音频加载逻辑（opus/mp3）
        } else {
          // 若成功加载视频：继续视频模式后续流程
        video.volume = muted ? 0 : volume;
        if (fadeInOut) {
          video.volume = 0;
        }
        video.playbackRate = 1.0;

        // 应用选择的音频输出设备（video 也支持 setSinkId）
        if (selectedAudioOutputId && typeof video.setSinkId === 'function') {
          try {
            const sinkId = selectedAudioOutputId === 'default' ? '' : selectedAudioOutputId;
            await video.setSinkId(sinkId);
          } catch (e) {
            mpWarn('[音频输出] 应用设备失败(视频):', e);
          }
        }

        // 若视频没加载成功（src 已被移除），不要再走“视频模式”
        if (video && video.src) {
          setDuration(video.duration || 0);
          durationRef.current = video.duration || 0;
        // 尝试通过 HEAD 获取视频文件大小（用于显示与码流估算）
        // 使用流式 URL 而不是原始文件路径
        let videoFileSize = null;
        try {
          const streamUrl = getStreamUrl(music, 'video');
          if (streamUrl) {
            const headResponse = await fetch(streamUrl, { method: 'HEAD', credentials: 'include' });
            const contentLength = headResponse.headers.get('Content-Length');
            if (contentLength) videoFileSize = parseInt(contentLength, 10);
          }
        } catch (_) {
          // 忽略错误，文件大小不是必需的
        }

        const videoExt = (() => {
          try {
            const p = String(videoFilePath || '').split('?')[0];
            const ext = p.split('.').pop();
            return ext ? String(ext).toLowerCase() : 'video';
          } catch (_) {
            return 'video';
          }
        })();

        setCurrentPlayingFormat({
          format: videoExt, // mp4/mkv/webm...
          path: videoFilePath,
          bitrate: null,
          file_size: videoFileSize,
          sample_rate: null,
          channels: null,
          codec: `${videoExt.toUpperCase()} Video`
        });

          setLoading(false);
          isLoadingRef.current = false;

        // 加载歌词（视频模式也需要）
        if (music.lyrics_type) {
          await loadLyrics(null, music.lyrics_type, music);
        } else {
          setLyrics([]);
        }

        // 缓存媒体文件和元数据（后台执行，不阻塞播放）
        if (music) {
          cacheMusicMetadata(music).then(() => {
            checkCacheStatus(music);
          }).catch(err => {
            console.warn('Failed to cache music metadata:', err);
          });

          // 播放视频时，不缓存视频，而是缓存对应的音频文件（确保离线时有歌可以听）
          // 视频文件较大，不做离线缓存；但需要缓存音频以便离线时播放
          const audioStreamUrlForVideo = getStreamUrlForCache(music, null, videoFilePath);
          if (audioStreamUrlForVideo) {
            cacheMusicFile(audioStreamUrlForVideo).then(() => {
              setTimeout(() => {
                checkCacheStatus(music);
                try {
                  window.dispatchEvent(new CustomEvent('tenryu:music-cached', {
                    detail: { musicId: music.id }
                  }));
                } catch (_) {}
              }, 500);
            }).catch(err => {
              console.warn('Failed to cache audio file for video:', err);
            });
          }

          // 缓存封面（使用流式URL）
          const coverStreamUrl = getCoverStreamUrlLocal(music);
          if (coverStreamUrl) {
            cacheMusicFile(coverStreamUrl).catch(err => {
              console.warn('Failed to cache cover image:', err);
            });
          }

          setTimeout(() => {
            checkCacheStatus(music);
          }, 3000);
        }

          if (shouldAutoPlay) {
            try {
              await video.play();
              updatePlaying(true);
              if (!isProgressTrackingRef.current) startProgressTracking();
              if (playHistoryIntervalRef.current === null) startPlayHistoryTracking();
              recordPlay();
              if (fadeInOut) {
                setIsVisualSwitching(false);
                setVisualFadeInToken((v) => v + 1);
                const targetVolume = muted ? 0 : volume;
                if (targetVolume > 0) {
                  let currentVol = 0;
                  const step = getFadeStep(0, targetVolume);
                  const fadeInterval = setInterval(() => {
                    currentVol += step;
                    if (currentVol >= targetVolume) {
                      video.volume = targetVolume;
                      clearInterval(fadeInterval);
                    } else {
                      video.volume = currentVol;
                    }
                  }, FADE_TICK_MS);
                }
              }
            } catch (e) {
              updatePlaying(false);
            }
          }
          return;
        }
        }
      }
      
      // 确定要加载的文件路径：优先 Opus，失败则回退到 MP3
      let loadedSuccessfully = false;
      let currentFormat = 'auto'; // 跟踪当前使用的格式，用于生成流式URL
      
      // 检查是否有转码格式，优先使用 Opus
      if (music.metadata && music.metadata.convertedFormats) {
        const opusFormat = music.metadata.convertedFormats.find(f => f.format === 'opus');
        if (opusFormat && opusFormat.path) {
          // 优先尝试 Opus
          try {
            const streamUrl = getStreamUrl(music, 'opus');
            audio.src = streamUrl; // 使用流式URL隐藏实际文件路径
            audio.volume = muted ? 0 : volume;
            audio.playbackRate = 1.0;
            
            // 应用选择的音频输出设备
            if (selectedAudioOutputId && typeof audio.setSinkId === 'function') {
              try {
                const sinkId = selectedAudioOutputId === 'default' ? '' : selectedAudioOutputId;
                await audio.setSinkId(sinkId);
              } catch (e) {
                mpWarn('[音频输出] 应用设备失败:', e);
              }
            }
            
            if (fadeInOut) {
              audio.volume = 0;
            }
            
            await tryLoadAudioFile(audio, streamUrl);
            loadedSuccessfully = true;
            
            // 如果转码格式中没有文件大小，尝试通过 HEAD 请求获取
            let opusFileSize = opusFormat.file_size;
            if (!opusFileSize) {
              try {
                const streamUrl = getStreamUrl(music, 'opus');
                const headResponse = await fetch(streamUrl, { method: 'HEAD' });
                const contentLength = headResponse.headers.get('Content-Length');
                if (contentLength) {
                  opusFileSize = parseInt(contentLength, 10);
                }
              } catch (e) {
                console.warn('Failed to get Opus file size:', e);
              }
            }
            
            // 从实际播放的文件计算码流：bitrate = (file_size * 8) / duration
            let actualBitrate = null;
            if (opusFileSize && audio.duration && audio.duration > 0) {
              actualBitrate = Math.round((opusFileSize * 8) / audio.duration); // bps
            }
            
            // 记录当前播放的格式信息
            setCurrentPlayingFormat({
              format: 'opus',
              path: opusFormat.path,
              bitrate: actualBitrate, // 从实际文件计算
              file_size: opusFileSize || null,
              sample_rate: music.sample_rate, // 使用原始采样率
              channels: music.channels,
              codec: 'Opus'
            });
            mpLog('Loaded Opus format:', opusFormat.path);
          } catch (opusError) {
            mpWarn('Failed to load Opus, falling back to MP3:', opusError);
            // Opus 加载失败，回退到 MP3
            const mp3Format = music.metadata.convertedFormats.find(f => f.format === 'mp3');
            if (mp3Format && mp3Format.path) {
              currentFormat = 'mp3'; // 标记使用 MP3 格式
              
              // 如果转码格式中没有文件大小，尝试通过 HEAD 请求获取
              let mp3FileSize = mp3Format.file_size;
              if (!mp3FileSize) {
                try {
                  const streamUrl = getStreamUrl(music, 'mp3');
                  const headResponse = await fetch(streamUrl, { method: 'HEAD' });
                  const contentLength = headResponse.headers.get('Content-Length');
                  if (contentLength) {
                    mp3FileSize = parseInt(contentLength, 10);
                  }
                } catch (e) {
                  console.warn('Failed to get MP3 file size:', e);
                }
              }
              
              // 从实际播放的文件计算码流：bitrate = (file_size * 8) / duration
              let actualBitrate = null;
              if (mp3FileSize && audio.duration && audio.duration > 0) {
                actualBitrate = Math.round((mp3FileSize * 8) / audio.duration); // bps
              }
              
              // 记录当前播放的格式信息（转码的 MP3）
              setCurrentPlayingFormat({
                format: 'mp3',
                path: mp3Format.path,
                bitrate: actualBitrate, // 从实际文件计算
                file_size: mp3FileSize || null,
                sample_rate: music.sample_rate,
                channels: music.channels,
                codec: music.metadata?.codec || 'MP3'
              });
            } else {
              // 如果没有 MP3 格式，使用原始 file_path
              currentFormat = 'auto'; // 使用自动选择
              // 记录当前播放的格式信息（原始文件）
              setCurrentPlayingFormat({
                format: music.file_format?.toLowerCase() || 'mp3',
                path: music.file_path,
                bitrate: music.bitrate,
                file_size: music.file_size,
                sample_rate: music.sample_rate,
                channels: music.channels,
                codec: music.metadata?.codec || null
              });
            }
          }
        }
      }
      
      // 如果 Opus 加载失败或没有 Opus 格式，使用 MP3 或原始文件
      if (!loadedSuccessfully) {
        const streamUrl = getStreamUrl(music, currentFormat);
        audio.src = streamUrl; // 使用流式URL隐藏实际文件路径
        audio.volume = muted ? 0 : volume;
        audio.playbackRate = 1.0;
        
        // 如果启用淡入效果，从0音量开始
        if (fadeInOut) {
          audio.volume = 0;
        }
        
        // 等待元数据加载
        await tryLoadAudioFile(audio, streamUrl);
        
        // 从实际播放的文件计算码流：bitrate = (file_size * 8) / duration
        let actualBitrate = music.bitrate; // 默认使用数据库中的码流
        if (music.file_size && audio.duration && audio.duration > 0) {
          // 如果可以从文件大小和时长计算，优先使用计算结果
          actualBitrate = Math.round((music.file_size * 8) / audio.duration); // bps
        }
        
        // 记录当前播放的格式信息（原始文件）
        setCurrentPlayingFormat({
          format: music.file_format?.toLowerCase() || 'mp3',
          path: music.file_path,
          bitrate: actualBitrate, // 从实际文件计算
          file_size: music.file_size,
          sample_rate: music.sample_rate,
          channels: music.channels,
          codec: music.metadata?.codec || null
        });
      }
      
      // 如果有视频文件，加载视频
      if (videoFilePath && video) {
        try {
          video.src = getStreamUrl(music, 'video'); // 使用流式URL隐藏实际文件路径
          video.volume = muted ? 0 : volume;
          video.playbackRate = 1.0;
          
          // 等待视频元数据加载
          await new Promise((resolve, reject) => {
            const onLoadedMetadata = () => {
              video.removeEventListener('loadedmetadata', onLoadedMetadata);
              video.removeEventListener('error', onError);
              // 获取视频原始尺寸
              if (video.videoWidth && video.videoHeight) {
                setVideoDimensions({ width: video.videoWidth, height: video.videoHeight });
              }
              resolve();
            };
            const onError = (e) => {
              console.error('Video load error:', e, 'File path:', videoFilePath);
              video.removeEventListener('loadedmetadata', onLoadedMetadata);
              video.removeEventListener('error', onError);
              reject(new Error('Video load failed'));
            };
            video.addEventListener('loadedmetadata', onLoadedMetadata);
            video.addEventListener('error', onError);
            
            // 设置超时
            setTimeout(() => {
              if (video.readyState === 0) {
                video.removeEventListener('loadedmetadata', onLoadedMetadata);
                video.removeEventListener('error', onError);
                reject(new Error('Video load timeout'));
              }
            }, 10000);
          });
          
          // 同步音频和视频的时长
          if (video.duration && video.duration > 0) {
            setDuration(video.duration);
            // 同步音频时长到视频时长
            if (audio) {
              audio.currentTime = 0;
            }
          }
          
          // 监听视频播放进度，同步到音频（用于进度条显示）
          video.addEventListener('timeupdate', () => {
            if (audio && video.currentTime !== audio.currentTime) {
              audio.currentTime = video.currentTime;
            }
          });
          
          // 注意：视频播放结束事件由 useEffect 中的统一 handleEnded 处理
          // 这里不再单独添加 ended 事件监听，避免状态冲突和重复处理
        } catch (videoError) {
          console.warn('Failed to load video, falling back to audio:', videoError);
          // 视频加载失败，继续使用音频
        }
      } else {
        // 没有视频，使用音频时长
        setDuration(audio.duration);
      }

      // 如果已经加载了文件，更新码流（因为此时 duration 已经确定）
      if (currentPlayingFormat && audio.duration && audio.duration > 0) {
        const fileSize = currentPlayingFormat.file_size;
        if (fileSize) {
          const calculatedBitrate = Math.round((fileSize * 8) / audio.duration);
          setCurrentPlayingFormat(prev => ({
            ...prev,
            bitrate: calculatedBitrate
          }));
        }
      }

      // 加载歌词
      if (music.lyrics_type) {
        await loadLyrics(null, music.lyrics_type, music);
      } else {
        setLyrics([]);
      }

      setLoading(false);
      isLoadingRef.current = false; // 标记加载完成
      
      // 缓存音乐文件和元数据（后台执行，不阻塞播放）
      if (music) {
        // 先保存元数据到 IndexedDB
        cacheMusicMetadata(music).then(() => {
          // 元数据保存后，检查缓存状态（此时音频文件可能还在缓存中）
          checkCacheStatus(music);
        }).catch(err => {
          console.warn('Failed to cache music metadata:', err);
        });
        
        // 缓存媒体文件（有视频优先视频；否则缓存当前播放格式/主文件）
        // 使用流式URL进行缓存
        const videoPathToCache = getVideoFilePath(music);
        const mediaStreamUrlToCache = getStreamUrlForCache(music, currentPlayingFormat, videoPathToCache);
        if (mediaStreamUrlToCache) {
          cacheMusicFile(mediaStreamUrlToCache).then(() => {
            // 音频文件缓存完成后，再次检查缓存状态
            setTimeout(() => {
              checkCacheStatus(music);
              // 通知音乐列表更新缓存状态
              try {
                window.dispatchEvent(new CustomEvent('tenryu:music-cached', { 
                  detail: { musicId: music.id } 
                }));
              } catch (_) {}
            }, 500); // 给 Service Worker 一点时间完成缓存
          }).catch(err => {
            console.warn('Failed to cache music file:', err);
          });
        }
        
        // 缓存封面（使用流式URL）
        const coverStreamUrl = getCoverStreamUrlLocal(music);
        if (coverStreamUrl) {
          cacheMusicFile(coverStreamUrl).catch(err => {
            console.warn('Failed to cache cover image:', err);
          });
        }
        
        // 延迟再次检查缓存状态（确保缓存完成）
        setTimeout(() => {
          checkCacheStatus(music);
        }, 3000);
      } else {
        // 如果没有音乐，直接检查当前音乐的缓存状态
        checkCacheStatus(music);
      }
      
      // 如果应该自动播放，在加载完成后开始播放（视频模式已在上方 return）
      if (shouldAutoPlay && audioRef.current) {
        try {
          await audioRef.current.play();
          updatePlaying(true);
          startProgressTracking();
          startPlayHistoryTracking();
          recordPlay();
          
          // 播放时立即应用独占模式，尽量独占声卡（音乐播放程序应该独占）
          if ('mediaSession' in navigator) {
            applyAudioFocusMode(true);
          }
          
          // 如果启用淡入效果
          if (fadeInOut) {
            const mediaElement = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
            if (mediaElement && mediaElement.volume === 0) {
              setIsVisualSwitching(false);
              setVisualFadeInToken((v) => v + 1);
              const targetVolume = muted ? 0 : volume;
              if (targetVolume <= 0) return;
              let currentVol = 0;
              const step = getFadeStep(0, targetVolume);
              const fadeInterval = setInterval(() => {
                currentVol += step;
                if (currentVol >= targetVolume) {
                  mediaElement.volume = targetVolume;
                  clearInterval(fadeInterval);
                } else {
                  mediaElement.volume = currentVol;
                }
              }, 50);
            }
          }
        } catch (playError) {
          // 忽略 AbortError（当加载新音频时，之前的播放请求会被中断，这是正常行为）
          if (playError.name !== 'AbortError') {
            console.error('Auto play after load error:', playError);
          }
          // 如果自动播放失败（可能是浏览器策略），不显示错误，让用户手动点击播放
        }
      }
    } catch (error) {
      console.error('Load music error:', error);
      console.error('Music file path:', music?.file_path);
      isLoadingRef.current = false; // 标记加载完成（即使失败）
      setLoading(false);
      updatePlaying(false);
      // 显示错误提示
      alert(error.message || t('hints.loadMusicFailed', { file: music?.file_path || t('hints.unknownFile') }));
    }
  };

  // 加载歌词
  const loadLyrics = async (lyricsFile, lyricsType, music = null) => {
    try {
      // 使用传入的 music 对象或 currentMusic 来生成流式URL
      const musicToUse = music || currentMusic;
      const streamUrl = musicToUse ? getLyricsStreamUrlLocal(musicToUse) : null;
      const urlToUse = streamUrl || lyricsFile; // 如果没有流式URL，回退到原始路径
      if (!urlToUse) return;
      
      // 隐藏歌词文件路径，只显示文件名
      const lyricsFileName = (lyricsFile || urlToUse).split('/').pop() || 'N/A';
      console.log('Loading lyrics file:', lyricsFileName, 'Type:', lyricsType);
      const response = await axios.get(urlToUse, { responseType: 'text' });
      const content = response.data;
      console.log('Lyrics file content length:', content.length, 'First 200 chars:', content.substring(0, 200));

      // ====== 关键修复：流式歌词可能返回的实际格式与 DB 中的 lyrics_type / lyrics_file 后缀不一致 ======
      // 例如：DB 记录为 .txt，但后端按优先级返回了 .lrc；此时若仍按 txt 解析，会把时间标签当普通文本显示出来。
      const inferLyricsType = () => {
        try {
          // 1) 若是走“统一流”歌词端点：优先用响应头提示的真实扩展名（由后端设置）
          const isUnifiedStream = typeof urlToUse === 'string' && urlToUse.includes('/api/music/') && urlToUse.includes('/stream') && urlToUse.includes('asset=lyrics');
          if (isUnifiedStream) {
            const ext = (response?.headers?.['x-lyrics-ext'] || response?.headers?.['X-Lyrics-Ext'] || '').toString().trim().toLowerCase();
            if (ext === '.lrc') return 'lrc';
            if (ext === '.srt') return 'srt';
            if (ext === '.txt') return 'txt';
          }

          // 2) 内容嗅探（兜底）：有时间标签就按 lrc/srt 解析
          if (typeof content === 'string') {
            if (/\[\d{1,2}:\d{2}(?:\.\d{2})?\]/.test(content)) return 'lrc';
            if (/\d{1,2}:\d{2}:\d{2},\d{3}\s*-->\s*\d{1,2}:\d{2}:\d{2},\d{3}/.test(content)) return 'srt';
          }

          // 3) 退回原有判断（DB 提示 / 文件名后缀）
          if (lyricsType === 'lrc' || (lyricsFile && lyricsFile.endsWith('.lrc'))) return 'lrc';
          if (lyricsType === 'srt' || (lyricsFile && lyricsFile.endsWith('.srt'))) return 'srt';
          return 'txt';
        } catch (_) {
          return 'txt';
        }
      };
      const effectiveType = inferLyricsType();
      
      // 缓存歌词文件（后台执行，使用流式URL）
      if (urlToUse) {
        // 播放器不支持 TXT：避免把 txt 形态缓存成“歌词”，减少后续误命中
        if (effectiveType !== 'txt') {
          cacheLyrics(urlToUse, content, effectiveType).catch(err => {
            console.warn('Failed to cache lyrics:', err);
          });
        }
      }

      let parsedLyrics = [];
      if (effectiveType === 'lrc') {
        parsedLyrics = parseLRC(content);
        console.log('Parsed LRC lyrics, count:', parsedLyrics.length, 'First few times:', parsedLyrics.slice(0, 5).map(l => l.time));
      } else if (effectiveType === 'srt') {
        parsedLyrics = parseSRT(content);
        console.log('Parsed SRT lyrics, count:', parsedLyrics.length, 'First few times:', parsedLyrics.slice(0, 5).map(l => l.time));
      } else if (effectiveType === 'txt') {
        // 需求：播放器里不允许使用 .txt 歌词（只支持带时间轴的 lrc/srt）
        console.warn('[Lyrics] TXT lyrics are not supported in player. Skipping display.', {
          musicId: currentMusic?.id,
          lyricsFile,
          urlToUse
        });
        parsedLyrics = [];
      } else {
        console.warn('Unknown lyrics type, not parsing:', lyricsType);
        parsedLyrics = [];
      }

      // 如果启用了翻译显示，尝试加载翻译
      if (lyricSettings.showTranslation && currentMusic) {
        try {
          const targetLang = resolveTargetTranslationLang(currentMusic);
          const translationResponse = await axios.get(`/api/music/${currentMusic.id}/lyrics/translation`, {
            params: { lang: targetLang }
          });
          if (translationResponse.data.success && translationResponse.data.data.translation) {
            const translationContent = translationResponse.data.data.translation;
            const translationFormat = translationResponse.data.data.format;
            
            let translations = [];
            if (translationFormat === 'lrc') {
              translations = parseLRC(translationContent);
            } else if (translationFormat === 'srt') {
              translations = parseSRT(translationContent);
            } else if (translationFormat === 'txt') {
              // 与主歌词一致：播放器不展示 TXT 翻译（避免时间轴对齐错误/显示异常）
              translations = [];
            }
            
            // 缓存翻译歌词
            if (currentMusic && translations.length > 0) {
              const translationContent = JSON.stringify(translations);
              cacheLyrics(`${lyricsFile}_translation_${targetLang}`, translationContent, lyricsType).catch(err => {
                console.warn('Failed to cache translation lyrics:', err);
              });
            }
            
            // 合并翻译到歌词
            parsedLyrics = parsedLyrics.map((lyric) => {
              const translation = translations.find(t => 
                Math.abs(t.time - lyric.time) < 0.5 // 0.5秒内的匹配
              );
              return {
                ...lyric,
                translation: translation ? translation.text : null
              };
            });
          }
        } catch (error) {
          console.warn('Load translation error:', error);
          // 翻译加载失败不影响主歌词显示
        }
      }

      setLyrics(parsedLyrics);
      lyricsRef.current = parsedLyrics; // 同时更新 ref
      // 重置当前歌词行（ref + DOM）
      clearLyricHighlight();
      updateMiniLyric(-1);
      currentLyricIndexRef.current = -1;
      console.log('Lyrics loaded and set, count:', parsedLyrics.length);
    } catch (error) {
      console.error('Load lyrics error:', error);
      setLyrics([]);
      lyricsRef.current = [];
      clearLyricHighlight();
      updateMiniLyric(-1);
      currentLyricIndexRef.current = -1;
    }
  };

  // 检查当前音乐是否有视频文件
  const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;

  // 播放/暂停
  const togglePlay = async () => {
    if (!currentMusic) return;

    try {
      if (playing) {
        // 暂停
        if (hasVideo && videoRef.current) {
          videoRef.current.pause();
        } else if (audioRef.current) {
          audioRef.current.pause();
        }
        updatePlaying(false);
        stopProgressTracking();
        stopPlayHistoryTracking();
      } else {
        // 播放
        const mediaElement = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
        if (muted) {
          const newVolume = volume || 0.5;
          setMuted(false);
          if (volume === 0) setVolume(newVolume);
          if (audioRef.current) audioRef.current.volume = newVolume;
          if (videoRef.current) videoRef.current.volume = newVolume;
        }
        if (mediaElement) {
          await mediaElement.play();
        }
        updatePlaying(true);
        startProgressTracking();
        startPlayHistoryTracking();
        recordPlay();
        
        // 播放时立即应用独占模式，尽量独占声卡（音乐播放程序应该独占）
        if ('mediaSession' in navigator) {
          applyAudioFocusMode(true);
        }
        
        // 如果启用淡入效果（音频/视频都支持）
        if (fadeInOut) {
          const mediaElement = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
          if (mediaElement && mediaElement.volume === 0) {
            setIsVisualSwitching(false);
            setVisualFadeInToken((v) => v + 1);
            const targetVolume = muted ? 0 : volume;
            if (targetVolume <= 0) return;
            let currentVol = 0;
            const step = getFadeStep(0, targetVolume);
            const fadeInterval = setInterval(() => {
              currentVol += step;
              if (currentVol >= targetVolume) {
                mediaElement.volume = targetVolume;
                clearInterval(fadeInterval);
              } else {
                mediaElement.volume = currentVol;
              }
            }, 50);
          }
        }
      }
    } catch (error) {
      console.error('Play error:', error);
    }
  };

  // 开始进度跟踪
  const getAllProgressTracks = () => {
    const root = playerRef.current;
    if (!root) return [];
    try {
      return Array.from(root.querySelectorAll('[data-progress-track]'));
    } catch (_) {
      return [];
    }
  };

  const updateAllProgressVisual = (pct) => {
    const clamped = Math.max(0, Math.min(1, pct));
    const tracks = getAllProgressTracks();
    if (!tracks || tracks.length === 0) return;
    for (const track of tracks) {
      const fill = track.querySelector('[data-progress-fill]');
      const knob = track.querySelector('[data-progress-knob]');
      if (fill) fill.style.width = `${clamped * 100}%`;
      if (knob) knob.style.left = `${clamped * 100}%`;
    }
  };

  const startProgressTracking = () => {
    if (isProgressTrackingRef.current) return;
    isProgressTrackingRef.current = true;
    
    // 进度条：用 rAF 直驱 DOM（播放时每帧更新，不触发 React 重渲染）
    const startProgressRaf = () => {
      if (progressRafRef.current) return;
      const tick = () => {
        const audio = audioRef.current;
        const video = videoRef.current;
        const timeElDesktop = currentTimeTextRefDesktop.current;
        const timeElMobile = currentTimeTextRefMobile.current;
        if (isSeekingRef.current) {
          progressRafRef.current = requestAnimationFrame(tick);
          return;
        }
        
        // 如果有视频，优先使用视频的进度；否则使用音频的进度
        const mediaElement = (hasVideo && video) ? video : audio;
        const dur = mediaElement?.duration || durationRef.current || 0;
        if (mediaElement && dur > 0) {
          const pct = Math.max(0, Math.min(1, mediaElement.currentTime / dur));
          updateAllProgressVisual(pct);
        }

        if (fadeInOut && (hasVideoInCurrentMusic || shouldShowCoverWindow) && mediaElement && dur > 0) {
          const remaining = Math.max(0, dur - (mediaElement.currentTime || 0));
          const fadeSeconds = (visualFadeMs || 0) / 1000;
          if (fadeSeconds > 0 && remaining <= fadeSeconds && !coverFadeOutStartedRef.current) {
            coverFadeOutStartedRef.current = true;
            if (coverActiveLayerRef.current === 'A') setCoverLayerAOpacity(0);
            else setCoverLayerBOpacity(0);
          }
        }

        // 当前时间文本：仅当秒数变化时更新，避免频繁触发 React 重渲染导致按钮卡顿
        if (mediaElement) {
          const sec = Math.floor(mediaElement.currentTime || 0);
          if (sec !== (lastTimeLabelSecRef.current ?? -1)) {
            const t = formatTime(sec);
            if (timeElDesktop) timeElDesktop.textContent = t;
            if (timeElMobile) timeElMobile.textContent = t;
            lastTimeLabelSecRef.current = sec;
          }
        }

        // 歌词同步：并入 rAF 循环，按时间节流（默认约 120ms 一次）
        if (mediaElement) {
          const nowMs = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
          if (nowMs - (lastLyricSyncMsRef.current || 0) >= 120) {
            const currentLyrics = lyricsRef.current || [];
            if (currentLyrics.length > 0) {
              const t = (mediaElement.currentTime || 0) + (lyricOffsetRef.current || 0);
              let idx = currentLyricIndexRef.current;
              if (idx < -1) idx = -1;
              if (idx >= currentLyrics.length) idx = currentLyrics.length - 1;
              while (idx + 1 < currentLyrics.length && t >= currentLyrics[idx + 1].time) idx++;
              while (idx >= 0 && t < currentLyrics[idx].time) idx--;
              currentLyricIndexRef.current = idx;
              if (idx !== currentLyricLineRef.current) {
                applyLyricHighlight(idx);
              }
            }
            lastLyricSyncMsRef.current = nowMs;
          }
        }
        progressRafRef.current = requestAnimationFrame(tick);
      };
      progressRafRef.current = requestAnimationFrame(tick);
    };

    startProgressRaf();
  };

  // 停止进度跟踪
  const stopProgressTracking = () => {
    isProgressTrackingRef.current = false;
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (progressRafRef.current) {
      cancelAnimationFrame(progressRafRef.current);
      progressRafRef.current = null;
    }
    hideSeekPreview();
  };

  // 开始播放历史跟踪
  const startPlayHistoryTracking = () => {
    if (playHistoryIntervalRef.current) return;
    
    let startTime = Date.now();
    
    playHistoryIntervalRef.current = setInterval(() => {
      const mediaElement = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
      if (mediaElement && currentMusic) {
        const currentPosition = mediaElement.currentTime;
        const playDuration = (Date.now() - startTime) / 1000;
        
        // 每 30 秒记录一次
        if (playDuration >= 30) {
          recordPlayProgress(currentPosition, playDuration);
          startTime = Date.now();
        }
      }
    }, 30000); // 30秒
  };

  // 停止播放历史跟踪
  const stopPlayHistoryTracking = () => {
    if (playHistoryIntervalRef.current) {
      clearInterval(playHistoryIntervalRef.current);
      playHistoryIntervalRef.current = null;
    }
  };

  // 记录播放
  const recordPlay = async () => {
    if (!currentMusic) return;
    
    try {
      const userId = getUserId();
      await axios.post(`/api/music/${currentMusic.id}/play`, {
        position: 0,
        duration: 0,
        completed: 0
      }, {
        headers: { 'X-User-ID': userId }
      });
    } catch (error) {
      console.error('Record play error:', error);
    }
  };

  // 记录播放进度
  const recordPlayProgress = async (position, playDuration) => {
    if (!currentMusic) return;
    
    try {
      const userId = getUserId();
      await axios.post(`/api/music/${currentMusic.id}/play`, {
        position: Math.floor(position),
        duration: Math.floor(playDuration),
        completed: 0
      }, {
        headers: { 'X-User-ID': userId }
      });
    } catch (error) {
      console.error('Record play progress error:', error);
    }
  };

  // 切歌前淡出（仅音频；视频不淡出）
  const fadeOutBeforeSwitch = useCallback((switchFn) => {
    if (!fadeInOut || !switchFn) {
      if (switchFn) switchFn();
      return;
    }
    const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
    const mediaElement = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
    if (!mediaElement || !playing || muted) {
      switchFn();
      return;
    }
    if (fadeOutTimerRef.current) {
      clearInterval(fadeOutTimerRef.current);
      fadeOutTimerRef.current = null;
      switchFn();
      return;
    }
    const isAtEnd = mediaElement.ended || (Number.isFinite(mediaElement.duration) && mediaElement.duration > 0 && mediaElement.currentTime >= mediaElement.duration - 0.05);
    if (isAtEnd) {
      switchFn();
      return;
    }
    let currentVol = mediaElement.volume;
    const startVol = currentVol;
    const step = getFadeStep(startVol, 0);
    if (currentVol <= 0) {
      switchFn();
      return;
    }
    setIsVisualSwitching(true);
    setVisualFadeOutToken((v) => v + 1);
    fadeOutTimerRef.current = setInterval(() => {
      currentVol -= step;
      if (currentVol <= 0) {
        mediaElement.volume = 0;
        clearInterval(fadeOutTimerRef.current);
        fadeOutTimerRef.current = null;
        switchFn();
      } else {
        mediaElement.volume = currentVol;
      }
    }, FADE_TICK_MS);
  }, [fadeInOut, currentMusic, muted, playing]);

  // 上一首
  const handlePrevious = useCallback(() => {
    if (!playlist || playlist.length === 0) return;
    
    const currentIndex = playlist.findIndex(m => m.id === currentMusic?.id);
    let nextIndex;
    
    if (playMode === 'random') {
      nextIndex = Math.floor(Math.random() * playlist.length);
    } else {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : playlist.length - 1;
    }
    
    if (onPrevious) {
      fadeOutBeforeSwitch(() => onPrevious(playlist[nextIndex]));
    }
  }, [playlist, currentMusic, playMode, onPrevious, fadeOutBeforeSwitch]);

  // 下一首
  const handleNext = useCallback(() => {
    if (!playlist || playlist.length === 0) return;
    
    const currentIndex = playlist.findIndex(m => m.id === currentMusic?.id);
    let nextIndex;
    
    if (playMode === 'random') {
      nextIndex = Math.floor(Math.random() * playlist.length);
    } else if (playMode === 'repeat') {
      nextIndex = currentIndex;
    } else {
      nextIndex = currentIndex < playlist.length - 1 ? currentIndex + 1 : 0;
    }
    
    if (onNext) {
      fadeOutBeforeSwitch(() => onNext(playlist[nextIndex]));
    }
  }, [playlist, currentMusic, playMode, onNext, fadeOutBeforeSwitch]);

  // 切换播放模式
  const togglePlayMode = () => {
    const modes = ['sequential', 'random', 'repeat'];
    const currentIndex = modes.indexOf(playMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setPlayMode(modes[nextIndex]);
  };

  // 设置进度（未使用，保留用于未来功能）
  // eslint-disable-next-line no-unused-vars
  const handleSeek = (e) => {
    const mediaElement = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
    if (!mediaElement) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const dur = mediaElement.duration || durationRef.current || duration || 0;
    const newTime = percentage * dur;
    
    mediaElement.currentTime = newTime;
    // 不再 setState 更新 currentTime（避免播放时额外重渲染）；时间显示由 rAF 直写 DOM
    // 立刻更新进度条视觉（避免等待下一帧/下一次 tick）
    requestAnimationFrame(() => {
      const dur2 = mediaElement?.duration || durationRef.current || 0;
      if (!dur2 || dur2 <= 0) return;
      const pct = Math.max(0, Math.min(1, newTime / dur2));
      updateAllProgressVisual(pct);
    });
  };

  // 进度条：iOS 风格丝滑拖动（pointer events）
  const updateProgressVisual = (pct) => {
    updateAllProgressVisual(pct);
  };

  const pctFromClientX = (clientX, trackEl) => {
    const rect = trackEl.getBoundingClientRect();
    const x = clientX - rect.left;
    return rect.width > 0 ? (x / rect.width) : 0;
  };

  const findNearestLyricIndex = (t, arr) => {
    if (!arr || arr.length === 0) return -1;
    // 找到最后一个 time <= t 的索引（binary search）
    let lo = 0;
    let hi = arr.length - 1;
    let best = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (arr[mid].time <= t) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    if (best < 0) return 0;
    if (best >= arr.length - 1) return best;
    const a = arr[best];
    const b = arr[best + 1];
    // 取更接近 t 的那一句
    return (Math.abs(t - a.time) <= Math.abs(b.time - t)) ? best : (best + 1);
  };

  const applyMarqueeIfNeeded = (el, animRef, options = {}) => {
    if (!el) return;
    const {
      speedPxPerSec = 42,
      pauseMs = 450
    } = options;

    // 清理上一次动画
    if (animRef.current) {
      try { animRef.current.cancel(); } catch {}
      animRef.current = null;
    }

    // 强制单行
    el.style.whiteSpace = 'nowrap';
    el.style.overflow = 'hidden';

    // 需要等 DOM 更新后再测量
    requestAnimationFrame(() => {
      const overflow = el.scrollWidth - el.clientWidth;
      if (overflow <= 4) {
        el.style.transform = 'translateX(0px)';
        return;
      }

      // iOS 风格：慢速往返滚动（不截断，保证“完整显示”）
      const distance = overflow + 12; // 额外留一点呼吸边距
      const duration = Math.max(1400, (distance / speedPxPerSec) * 1000);

      try {
        animRef.current = el.animate(
          [
            { transform: 'translateX(0px)' },
            { transform: `translateX(${-distance}px)` }
          ],
          {
            duration,
            iterations: Infinity,
            direction: 'alternate',
            easing: 'ease-in-out',
            delay: pauseMs
          }
        );
      } catch {
        // 兜底：不支持 WAAPI 的环境直接保持不动
        el.style.transform = 'translateX(0px)';
      }
    });
  };

  const updateSeekPreview = (clientX, pct, dur, trackEl) => {
    const track = trackEl || progressTrackRef.current;
    if (!track || !dur || dur <= 0) return;

    // 气泡使用 portal 到 body：避免被播放器 overflow:hidden 裁剪
    const box = seekPreviewRef.current;
    if (!box) return;
    
    // 找到对应的 time 和 text 元素
    const timeEl = box.querySelector('[data-seek-preview-time]') || seekPreviewTimeRef.current;
    const textEl = box.querySelector('[data-seek-preview-text]') || seekPreviewTextRef.current;
    const transEl = box.querySelector('[data-seek-preview-trans]') || seekPreviewTransRef.current;
    
    if (!textEl) return;

    const lyricsArr = lyricsRef.current;
    if (!lyricsArr || lyricsArr.length === 0) return;

    // 进度条预览时间（用于显示 mm:ss）
    const tRaw = Math.max(0, Math.min(dur, pct * dur));
    // 歌词匹配时间：需要叠加 lyricOffset（播放同步逻辑也是这么做的）
    const tForLyric = tRaw + (lyricOffsetRef.current || 0);

    const idx = findNearestLyricIndex(tForLyric, lyricsArr);
    const line = lyricsArr[idx];
    if (!line || !line.text) return;

    // 缓存 rect，避免频繁调用 getBoundingClientRect（每 100ms 更新一次）
    const now = Date.now();
    let rect = progressTrackRectRef.current;
    if (!rect || now - progressTrackRectTimestampRef.current > 100) {
      rect = track.getBoundingClientRect();
      progressTrackRectRef.current = rect;
      progressTrackRectTimestampRef.current = now;
    }
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));

    // 位置：跟随手指，稍微夹紧到左右边缘（避免超出）
    // 气泡为 fixed 定位：left 需要转换成 viewport 坐标
    const leftInTrack = Math.max(12, Math.min(rect.width - 12, x));
    const left = rect.left + leftInTrack;
    // 顶点（箭头）高度为 8px：让“箭头尖”贴近进度条上边沿
    const top = Math.max(8, rect.top - 8); // transform 会把气泡整体抬到轨道上方
    box.style.top = `${top}px`;

    // 液态跟随：只更新 target，实际 left 由 rAF 逐帧 lerp 到位
    seekPreviewTargetXRef.current = left;
    if (!seekPreviewVisibleRef.current) {
      seekPreviewXRef.current = left;
      box.style.left = `${left}px`;
    }

    seekPreviewVisibleRef.current = true;
    box.style.opacity = '1';
    box.style.transform = 'translateX(-50%) translateY(-100%) translateY(-8px) scale(1)';

    // 取消隐藏定时器（避免拖动过程中闪烁）
    if (seekPreviewHideTimerRef.current) {
      clearTimeout(seekPreviewHideTimerRef.current);
      seekPreviewHideTimerRef.current = null;
    }

    // 金色主题：更明显的琥珀金描边/高光（不依赖 Tailwind 动态 class）
    box.style.borderColor = `rgba(${goldRGB}, 0.44)`;
    box.style.boxShadow = `0 18px 42px rgba(0,0,0,0.55), 0 0 26px rgba(${goldRGB2}, 0.22)`;

    if (timeEl) {
      // 显示 mm:ss（seek 预览时间）
      const mins = Math.floor(tRaw / 60);
      const secs = Math.floor(tRaw % 60);
      timeEl.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
      timeEl.style.backgroundColor = `rgba(${goldRGB2}, 0.22)`;
      timeEl.style.borderColor = `rgba(${goldRGB}, 0.42)`;
    }

    textEl.textContent = line.text;
    applyMarqueeIfNeeded(textEl, seekPreviewTextAnimRef, { speedPxPerSec: 44, pauseMs: 520 });

    if (transEl) {
      if (lyricSettings.showTranslation && line.translation) {
        transEl.textContent = line.translation;
        transEl.style.display = '';
        applyMarqueeIfNeeded(transEl, seekPreviewTransAnimRef, { speedPxPerSec: 46, pauseMs: 520 });
      } else {
        transEl.textContent = '';
        transEl.style.display = 'none';
        if (seekPreviewTransAnimRef.current) {
          try { seekPreviewTransAnimRef.current.cancel(); } catch {}
          seekPreviewTransAnimRef.current = null;
        }
      }
    }

    // 保存当前活动的气泡窗口引用，供 tick 函数使用
    currentSeekPreviewBoxRef.current = box;

    // 同步“歌词 Hint”（mini lyric）：拖动时跟随预览位置
    // 只在 idx 变化时更新，避免 pointermove 高频导致动画抖动/卡顿
    if (seekPreviewLyricIdxRef.current !== idx) {
      seekPreviewLyricIdxRef.current = idx;
      updateMiniLyric(idx);
    }
    
    // 启动浮窗动画循环（仅拖动时运行）
    if (!seekPreviewRafRef.current) {
      const tick = () => {
        // 找到当前活动的气泡窗口（优先使用保存的引用）
        let el = currentSeekPreviewBoxRef.current;
        if (!el) {
          // 如果保存的引用失效，查找所有可见的气泡窗口并更新第一个
          const allBoxes = document.querySelectorAll('[data-seek-preview]');
          if (allBoxes.length > 0) {
            el = allBoxes[0];
          } else {
            el = seekPreviewRef.current;
          }
        }
        
        if (!el) {
          seekPreviewRafRef.current = null;
          return;
        }

        const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        const prevTs = seekPreviewLastTsRef.current || now;
        const dt = Math.max(8, Math.min(34, now - prevTs)); // clamp dt，避免极端抖动
        seekPreviewLastTsRef.current = now;

        // 计算"目标速度"，快拖更跟手、慢拖更黏（更像 iOS）
        const tgt = seekPreviewTargetXRef.current || 0;
        const prevTgt = seekPreviewLastTargetXRef.current || tgt;
        const v = (tgt - prevTgt) / dt; // px/ms
        seekPreviewLastTargetXRef.current = tgt;

        // 平滑速度，避免 jitter
        const vel = (seekPreviewVelRef.current || 0) * 0.75 + v * 0.25;
        seekPreviewVelRef.current = vel;
        const speed = Math.abs(vel); // px/ms

        // 动态 lerp：0.18 ~ 0.42
        const lerp = Math.max(0.18, Math.min(0.42, 0.18 + (speed / 2.4) * 0.24));

        // 轻微阻尼追随：iOS "液态"感
        const cur = seekPreviewXRef.current || tgt;
        const next = cur + (tgt - cur) * lerp;
        seekPreviewXRef.current = next;
        el.style.left = `${next}px`;

        // 极轻的"速度呼吸缩放"，让浮窗更像系统浮层（0 ~ 0.02）
        if (seekPreviewVisibleRef.current) {
          const scale = 1 + Math.max(0, Math.min(0.02, speed * 0.012));
          // 保持“在进度条上方”的基准 transform，不要被 tick 覆盖掉
          el.style.transform = `translateX(-50%) translateY(-100%) translateY(-8px) scale(${scale.toFixed(3)})`;
        }

        // 只有在可见或正在拖动时继续
        if (seekPreviewVisibleRef.current || isSeekingRef.current) {
          seekPreviewRafRef.current = requestAnimationFrame(tick);
        } else {
          seekPreviewRafRef.current = null;
        }
      };
      seekPreviewRafRef.current = requestAnimationFrame(tick);
    }
  };

  const hideSeekPreview = () => {
    // 隐藏所有歌词气泡窗口（桌面版和手机版）
    const allBoxes = document.querySelectorAll('[data-seek-preview]');
    if (allBoxes.length > 0) {
      allBoxes.forEach(box => {
        box.style.opacity = '0';
        box.style.transform = 'translateX(-50%) translateY(-100%) translateY(-2px) scale(0.98)';
      });
    } else {
      // 兜底：如果没有找到，使用 ref
      const box = seekPreviewRef.current;
      if (box) {
        box.style.opacity = '0';
        box.style.transform = 'translateX(-50%) translateY(-100%) translateY(-2px) scale(0.98)';
      }
    }

    seekPreviewVisibleRef.current = false;

    if (seekPreviewTextAnimRef.current) {
      try { seekPreviewTextAnimRef.current.cancel(); } catch {}
      seekPreviewTextAnimRef.current = null;
    }
    if (seekPreviewTransAnimRef.current) {
      try { seekPreviewTransAnimRef.current.cancel(); } catch {}
      seekPreviewTransAnimRef.current = null;
    }

    // 过渡结束后停止 rAF（留一点时间让 opacity/transform 完成）
    if (seekPreviewHideTimerRef.current) {
      clearTimeout(seekPreviewHideTimerRef.current);
    }
    seekPreviewHideTimerRef.current = setTimeout(() => {
      if (!seekPreviewVisibleRef.current && seekPreviewRafRef.current) {
        cancelAnimationFrame(seekPreviewRafRef.current);
        seekPreviewRafRef.current = null;
      }
    }, 180);
  };

  const commitSeek = (pct) => {
    const mediaElement = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
    const dur = mediaElement?.duration || durationRef.current || 0;
    if (!mediaElement || !dur || dur <= 0) return;
    const clamped = Math.max(0, Math.min(1, pct));
    const newTime = clamped * dur;
    mediaElement.currentTime = newTime;
    // 不再 setState 更新 currentTime（避免播放时额外重渲染）；时间显示由 rAF 直写 DOM
    // 让歌词同步从当前位置重新校准（避免拖动后索引残留）
    currentLyricIndexRef.current = -1;
  };

  const handleSeekPointerDown = (e) => {
    const track = e.currentTarget;
    const mediaElement = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
    const dur = mediaElement?.duration || durationRef.current || 0;
    if (!track || !dur || dur <= 0) return;

    e.preventDefault();
    e.stopPropagation();

    isSeekingRef.current = true;
    seekPointerIdRef.current = e.pointerId;

    try {
      track.setPointerCapture(e.pointerId);
    } catch {}

    const pct = pctFromClientX(e.clientX, track);
    updateProgressVisual(pct);
    updateSeekPreview(e.clientX, pct, dur, track);

    const knob = progressKnobRef.current;
    if (knob) knob.style.opacity = '1';
  };

  const handleSeekPointerMove = (e) => {
    const track = e.currentTarget;
    if (!track || !isSeekingRef.current) return;
    if (seekPointerIdRef.current !== null && e.pointerId !== seekPointerIdRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    const pct = pctFromClientX(e.clientX, track);
    updateProgressVisual(pct);
    const mediaElement = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
    const dur = mediaElement?.duration || durationRef.current || 0;
    updateSeekPreview(e.clientX, pct, dur, track);
  };

  const handleSeekPointerUpOrCancel = (e) => {
    const track = e.currentTarget;
    if (!track || !isSeekingRef.current) return;
    if (seekPointerIdRef.current !== null && e.pointerId !== seekPointerIdRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    const pct = pctFromClientX(e.clientX, track);
    updateProgressVisual(pct);
    commitSeek(pct);
    hideSeekPreview();

    isSeekingRef.current = false;
    seekPointerIdRef.current = null;
    seekPreviewLyricIdxRef.current = -1;

    try {
      track.releasePointerCapture(e.pointerId);
    } catch {}

    // 滑块保持显示，不再恢复为透明
    // const knob = progressKnobRef.current;
    // if (knob) knob.style.opacity = '';
  };

  // 设置音量
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    // 如果有视频，也设置视频音量
    if (hasVideo && videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setMuted(newVolume === 0);
  };

  // 切换静音
  const toggleMute = () => {
    const mediaElement = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
    if (mediaElement) {
      if (muted) {
        const newVolume = volume || 0.5;
        mediaElement.volume = newVolume;
        if (audioRef.current) audioRef.current.volume = newVolume;
        if (hasVideo && videoRef.current) videoRef.current.volume = newVolume;
        setMuted(false);
      } else {
        mediaElement.volume = 0;
        if (audioRef.current) audioRef.current.volume = 0;
        if (hasVideo && videoRef.current) videoRef.current.volume = 0;
        setMuted(true);
      }
    }
  };

  // 清理
  useEffect(() => {
    return () => {
      stopProgressTracking();
      stopPlayHistoryTracking();
      if (seekPreviewHideTimerRef.current) {
        clearTimeout(seekPreviewHideTimerRef.current);
        seekPreviewHideTimerRef.current = null;
      }
      if (seekPreviewRafRef.current) {
        cancelAnimationFrame(seekPreviewRafRef.current);
        seekPreviewRafRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // durationRef 同步最新 duration，避免 rAF 闭包读到旧值
  useEffect(() => {
    durationRef.current = duration || 0;
  }, [duration]);

  // duration/切歌时同步一次进度条视觉，避免初始停在 0%
  useEffect(() => {
    const audio = audioRef.current;
    const fill = progressFillRef.current;
    const knob = progressKnobRef.current;
    const dur = audio?.duration || durationRef.current || 0;
    if (!audio || !fill || !knob || !dur || dur <= 0) return;
    const pct = Math.max(0, Math.min(1, audio.currentTime / dur));
    fill.style.width = `${pct * 100}%`;
    knob.style.left = `${pct * 100}%`;
  }, [duration, currentMusic?.id]);

  // 音频结束事件
  useEffect(() => {
    const mediaElement = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
    if (!mediaElement) return;

    const handleEnded = () => {
      console.log('[MusicPlayer] 播放结束事件触发', { currentMusic: currentMusic?.id, playMode, playlistLoop, playlistLength: playlist?.length });
      
      stopProgressTracking();
      stopPlayHistoryTracking();
      
      // 确保媒体元素已暂停（播放结束后媒体元素可能仍处于播放状态）
      if (mediaElement && !mediaElement.paused) {
        mediaElement.pause();
      }
      
      // 记录播放完成
      if (currentMusic) {
        const userId = getUserId();
        axios.post(`/api/music/${currentMusic.id}/play`, {
          position: duration,
          duration: duration,
          completed: 1
        }, {
          headers: { 'X-User-ID': userId }
        }).catch(console.error);
      }
      
      // 自动播放下一首
      if (playMode !== 'repeat') {
        // 关键点：顺序/随机切歌时不要把 playing 置为 false，
        // 否则下一首只会切换显示但不会自动播放（依赖 externalPlaying=true 触发 auto play）
        if (playlist && playlist.length > 0) {
          const currentIndex = playlist.findIndex(m => m.id === currentMusic?.id);
          // 判断是否有下一首：
          // - 随机模式：总是有下一首
          // - 顺序模式：如果开启播放列表循环，总是有下一首；否则检查是否不是最后一首
          let hasNext = false;
          if (playMode === 'random') {
            hasNext = true; // 随机模式总是有下一首
          } else if (playMode === 'sequential') {
            if (playlistLoop) {
              hasNext = true; // 开启循环播放，总是有下一首
            } else {
              hasNext = currentIndex >= 0 && currentIndex < playlist.length - 1; // 未开启循环，检查是否不是最后一首
            }
          }
          
          console.log('[MusicPlayer] 播放结束 - 判断下一首', { hasNext, currentIndex, playlistLength: playlist.length, playMode, playlistLoop });
          
          if (hasNext) {
            // 有下一首，继续播放
            console.log('[MusicPlayer] 有下一首，继续播放');
            
            // 计算下一首的索引
            let nextIndex;
            if (playMode === 'random') {
              nextIndex = Math.floor(Math.random() * playlist.length);
            } else if (playMode === 'repeat') {
              nextIndex = currentIndex;
            } else {
              nextIndex = currentIndex < playlist.length - 1 ? currentIndex + 1 : 0;
            }
            const nextMusic = playlist[nextIndex];
            
            // 如果下一首是同一首歌（播放列表只有一首或重复模式），直接重新播放
            if (nextMusic && nextMusic.id === currentMusic?.id) {
              console.log('[MusicPlayer] 下一首是同一首歌，直接重新播放');
              // 重置媒体元素并重新播放
              if (mediaElement) {
                try {
                  coverFadeOutStartedRef.current = false;
                  mediaElement.currentTime = 0;
                  if (fadeInOut) {
                    if (hasVideo && videoRef.current) {
                      videoRef.current.volume = 0;
                    } else if (audioRef.current) {
                      audioRef.current.volume = 0;
                    }
                    if (isSingleCoverMode) {
                      coverActiveLayerRef.current = 'A';
                      setCoverLayerAOpacity(0);
                    }
                  }
                  const playPromise = mediaElement.play();
                  if (playPromise !== undefined) {
                    playPromise
                      .then(() => {
                        updatePlaying(true);
                        startProgressTracking();
                        startPlayHistoryTracking();
                        if (fadeInOut) {
                          setIsVisualSwitching(false);
                          setVisualFadeInToken((v) => v + 1);
                          const targetVolume = muted ? 0 : volume;
                          if (targetVolume > 0) {
                            let currentVol = 0;
                            const step = getFadeStep(0, targetVolume);
                            const fadeInterval = setInterval(() => {
                              currentVol += step;
                              if (currentVol >= targetVolume) {
                                mediaElement.volume = targetVolume;
                                clearInterval(fadeInterval);
                              } else {
                                mediaElement.volume = currentVol;
                              }
                            }, FADE_TICK_MS);
                          }
                        }
                      })
                      .catch(err => {
                        updatePlaying(false);
                        console.error('Replay failed:', err);
                      });
                  }
                } catch (err) {
                  updatePlaying(false);
                  console.error('Replay failed:', err);
                }
              }
            } else {
              // 下一首是不同歌曲，调用 handleNext 切换
              handleNext();
              // 使用 setTimeout 确保 currentMusic 更新后再设置 playing 状态
              // 这样 externalPlaying 会触发自动播放逻辑
              setTimeout(() => {
                updatePlaying(true);
                setContextPlaying(true);
              }, 0);
            }
          } else {
            // 没有下一首（顺序模式最后一首且未开启循环），停止播放
            console.log('[MusicPlayer] 没有下一首，停止播放');
            // 确保媒体元素已停止
            if (mediaElement) {
              try {
                mediaElement.pause();
                mediaElement.currentTime = mediaElement.duration || 0; // 确保进度到末尾
              } catch (_) {}
            }
            updatePlaying(false);
            setShowLyrics(false);
          }
        } else {
          // 没有播放列表，停止播放
          console.log('[MusicPlayer] 没有播放列表，停止播放');
          // 确保媒体元素已停止
          if (mediaElement) {
            try {
              mediaElement.pause();
              mediaElement.currentTime = mediaElement.duration || 0; // 确保进度到末尾
            } catch (_) {}
          }
          updatePlaying(false);
          setShowLyrics(false);
        }
      } else {
        // 重复播放当前首
        console.log('[MusicPlayer] 重复模式，重新播放当前首');
        mediaElement.currentTime = 0;
        const playPromise = mediaElement.play();
        if (playPromise !== undefined) {
          playPromise.catch(err => {
            // 播放失败时也要更新状态
            updatePlaying(false);
            console.error('Repeat play failed:', err);
          });
        }
        updatePlaying(true);
        startProgressTracking();
        startPlayHistoryTracking();
      }
    };

    mediaElement.addEventListener('ended', handleEnded);
    return () => mediaElement.removeEventListener('ended', handleEnded);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMusic, duration, playMode, playlist, playlistLoop, handleNext]);

  // 设置睡眠定时器
  const setSleepTimerHandler = (minutes) => {
    if (sleepTimer) {
      clearTimeout(sleepTimer);
      setSleepTimer(null);
      setSleepTimerMinutes(0);
      return;
    }
    
    if (minutes <= 0) return;
    
    setSleepTimerMinutes(minutes);
    const timer = setTimeout(() => {
      const mediaElement = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
      if (!mediaElement) return;

      // 视频模式：直接暂停视频（视频里已包含音乐）
      if (hasVideo) {
        try { mediaElement.pause(); } catch (_) {}
        updatePlaying(false);
      } else if (fadeInOut) {
        // 音频/视频淡出
        const mediaElement = (hasVideo && videoRef.current) ? videoRef.current : audioRef.current;
        if (mediaElement) {
          let currentVol = mediaElement.volume;
          const startVol = currentVol;
          const step = getFadeStep(startVol, 0);
          const fadeInterval = setInterval(() => {
            currentVol -= step;
            if (currentVol <= 0) {
              mediaElement.volume = 0;
              clearInterval(fadeInterval);
              mediaElement.pause();
              updatePlaying(false);
            } else {
              mediaElement.volume = currentVol;
            }
          }, FADE_TICK_MS);
        }
      } else {
        try { mediaElement.pause(); } catch (_) {}
        updatePlaying(false);
      }
      setSleepTimer(null);
      setSleepTimerMinutes(0);
      alert(t('hints.sleepTimerStopped'));
    }, minutes * 60 * 1000);
    
    setSleepTimer(timer);
  };

  // 获取用户ID
  const getUserId = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'user_id') return value;
    }
    const userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    document.cookie = `user_id=${userId}; path=/; max-age=31536000`;
    return userId;
  };

  // 下载音乐（打开下载对话框）
  const handleDownload = () => {
    if (!currentMusic) return;
    
    if (!currentMusic.allow_download) {
      alert(t('musicList.downloadNotAllowed'));
      return;
    }
    
    setDownloadDialogOpen(true);
  };

  // 确认下载（从对话框）
  const handleDownloadConfirm = async (formats, includeLyrics) => {
    if (!currentMusic) return;
    
    // 使用统一的下载函数
    await downloadMusic(currentMusic.id, currentMusic.allow_download, null, formats, includeLyrics);
  };

  // 分享音乐
  const handleShare = async () => {
    if (!currentMusic) return;
    
    // 获取当前语言（从 i18n 或 URL 路径）
    const seg0 = window.location.pathname
      .split('/')
      .filter(Boolean)
      .find((seg) => ['cn', 'tw', 'ja', 'en', ZH_CN, ZH_TW, ZH_CN_LC, ZH_TW_LC].includes(seg));
    const segCanonical =
      seg0 === ZH_CN || seg0 === ZH_CN_LC ? 'cn' :
      seg0 === ZH_TW || seg0 === ZH_TW_LC ? 'tw' :
      seg0;
    const currentLang = i18n?.language || segCanonical || 'cn';
    
    // 使用统一的分享函数
    await shareMusic(currentMusic.id, currentLang);
  };

  // 格式化文件大小
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return t('player.unknown');
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // 格式化码流/采样率（抽离到独立模块；这里保留 i18n 的 unknown 文案）
  const formatBitrate = (bitrate) => formatBitrateRaw(bitrate, t('player.unknown'));
  const formatSampleRate = (sampleRate) => formatSampleRateRaw(sampleRate, t('player.unknown'));

  // 无音乐时：底部小条，不占用页面大面积空间
  // 重要：用 portal 挂到 body，确保永远浮在视口底部（不受页面滚动/父容器影响）
  if (!currentMusic) {
    if (typeof document === 'undefined') return null;
    return createPortal(
      <div
        ref={playerRef}
        className="bg-gray-900/70 text-white shadow-lg backdrop-blur-sm"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          paddingBottom: 'env(safe-area-inset-bottom)'
        }}
      >
        <div className="px-3 py-2 text-center">
          <p className="text-xs text-gray-300/80">{t('player.noMusicSelected')}</p>
        </div>
      </div>,
      document.body
    );
  }

  if (typeof document === 'undefined') return null;

  return (
    <>
      {/* 进度条歌词 Hint：独立 portal 到 body，避免被播放器裁剪 */}
      {createPortal(
        <div
          ref={seekPreviewRef}
          data-seek-preview
          aria-hidden="true"
          className="relative pointer-events-none select-none px-2.5 py-2 rounded-xl border border-amber-400/30 bg-gray-900/90 backdrop-blur-md shadow-[0_18px_42px_rgba(0,0,0,0.55)] w-[min(320px,80vw)] max-w-[360px]"
          style={{
            position: 'fixed',
            left: '50%',
            top: '0px',
            zIndex: 100000,
            opacity: 0,
            transform: 'translateX(-50%) translateY(-100%) translateY(-8px) scale(0.98)'
          }}
        >
          <div className="flex items-center gap-2">
            <span
              ref={seekPreviewTimeRef}
              data-seek-preview-time
              className="text-[11px] tabular-nums px-2 py-0.5 rounded-lg border border-amber-400/25 bg-amber-500/10 text-amber-100"
            >
              0:00
            </span>
          </div>
          <div
            ref={seekPreviewTextRef}
            data-seek-preview-text
            className="mt-1 text-[12px] leading-snug text-white whitespace-nowrap overflow-hidden"
            style={{ maxWidth: '100%' }}
          >
            &nbsp;
          </div>
          <div
            ref={seekPreviewTransRef}
            data-seek-preview-trans
            className="mt-0.5 text-[11px] leading-snug text-white/70 whitespace-nowrap overflow-hidden"
            style={{ display: 'none', maxWidth: '100%' }}
          >
            &nbsp;
          </div>

          {/* 底部三角：顶点对准进度条上边沿 */}
          <div
            data-seek-preview-arrow
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '50%',
              bottom: '-8px',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '8px solid rgba(17,24,39,0.90)'
            }}
          />
        </div>,
        document.body
      )}

      <PlayerPortals
        playerRef={playerRef}
        audioRef={audioRef}
        isFullscreen={isFullscreen}
        playing={playing}
        autoHidden={autoHidden}
        manualHidden={manualHidden}
        isLoading={loading}
        onMouseMove={bumpAutoHideCountdown}
        onMouseEnter={() => setIsPlayerHovering(true)}
        onMouseLeave={() => setIsPlayerHovering(false)}
        onBlankClick={handlePlayerBlankClick}
        videoWindowProps={{
          coverLayerAUrl,
          coverLayerBUrl,
          coverLayerAType,
          coverLayerBType,
          coverLayerAOpacity,
          coverLayerBOpacity,
          coverLayerANoFadeIn,
          coverLayerBNoFadeIn,
          showCover: !!(coverUrl || nextCoverUrl),
          visualFadeMs,
          visualFadeOutToken,
          visualFadeInToken,
          shouldShowVideoWindow,
          videoDockStyle,
          videoWindowStyle,
          dockingReady,
          isVideoFullscreen,
          setIsVideoFullscreen,
          shouldShowVideoSubtitles,
          videoRef,
          miniLyricTextRef,
          miniLyricTransTextRef,
          autoHidden,
          setAutoHidden,
          showPlaylist,
          setShowPlaylist,
          setShowVideoWindow
        }}
        panelProps={{
        t,
        i18n,
        cx,
        spaNavigate,
        showUiHint,
        devViewport,
        // video / fullscreen toggles
        videoWindowStyle,
        showVideoWindow,
        setShowVideoWindow,
        setIsVideoFullscreen,
        setIsFullscreen,
        // refs
        videoRef,
        miniLyricTextRef,
        miniLyricTransTextRef,
        currentTimeTextRefDesktop,
        currentTimeTextRefMobile,
        progressTrackRef,
        progressFillRef,
        progressKnobRef,
        seekPreviewRef,
        seekPreviewTimeRef,
        seekPreviewTextRef,
        seekPreviewTransRef,
        lyricsContainerRef,
        lyricLineRefs,
        currentLyricLineRef,
        settingsButtonRef,
        settingsPanelRef,
        playlistPanelRef,
        desktopControlsRowRef,
        volumeSliderWrapRef,
        // state
        currentMusic,
        playlist,
        playing,
        loading,
        duration,
        isFullscreen,
        isVideoFullscreen,
        isMobileViewport,
        isMdToLg,
        isLgUp,
        hideVolumeSlider,
        isMinimized,
        hasVideoInCurrentMusic,
        hasVideo,
        showLyrics,
        setShowLyrics,
        showPlaylist,
        setShowPlaylist,
        showSettings,
        setShowSettings,
        autoHidden,
        setAutoHidden,
        setManualHidden,
        isCached,
        showFileInfo,
        setShowFileInfo,
        fileInfoPosition,
        setFileInfoPosition,
        currentPlayingFormat,
        shouldShowMiniLyrics,
        lyrics,
        lyricOffset,
        lyricsViewportHeight,
        lyricSettings,
        setLyricSettings,
        highlightTheme,
        playMode,
        muted,
        volume,
        playlistLoop,
        setPlaylistLoop,
        sleepTimer,
        sleepTimerMinutes,
        fadeInOut,
        setFadeInOut,
        selectedAudioOutputId,
        setSelectedAudioOutputId,
        audioOutputDevices,
        audioFocusMode,
        setAudioFocusMode,
        // handlers/helpers
        getCoverStreamUrlLocal,
        getCurrentLanguageTitle,
        formatFileSize,
        formatBitrate,
        formatSampleRate,
        formatTime,
        handleButtonClickWithHint,
        handlePrevious,
        handleNext,
        togglePlay,
        handleSeekPointerDown,
        handleSeekPointerMove,
        handleSeekPointerUpOrCancel,
        togglePlayMode,
        handleVolumeChange,
        toggleMute,
        handleDownload,
        handleShare,
        ensureVideoMediaInfo,
        onPlaylistChange: handlePlaylistChange,
        onRemoveFromPlaylist: handleRemoveFromPlaylist,
        setPlaylist: handleSetPlaylist,
        setSleepTimerHandler,
        setAudioOutputDevice,
        enumerateAudioOutputDevices,
        isMobileDevice,
        handleDownloadConfirm,
        downloadDialogOpen,
        setDownloadDialogOpen
      }}
        dockProps={{
          playing,
          isFullscreen,
          autoHidden,
          manualHidden,
          title: getCurrentLanguageTitle(currentMusic),
          album: currentMusic?.album || '',
          hasVideo: hasVideoInCurrentMusic,
          isInstrumental: isInstrumentalMusic(currentMusic),
          onTogglePlay: togglePlay,
          onBump: bumpAutoHideCountdown,
          setAutoHidden,
          setManualHidden
        }}
      />
    </>
  );
};

export default MusicPlayer;

